import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  loadScreenerState,
  saveScreenerState,
  clearScreenerState,
  mergeScreenerFilterValues,
} from "../../../../utils/screenerStateStorage";
import { getPaginationWindow } from "../../../../utils/pagination";
import {
  buildRegionFilterOptions,
  normalizeScreenerStock,
  resolveStockRegion,
  isCountryCode,
} from "../../../../utils/invelpsRegions";

// ── DESIGN SYSTEM ─────────────────────────────────────────────────────────────
const C = {
  green:  "#6f8a5b", green2: "#8da57a", olive: "#a9b38a",
  amber:  "#caa85e", orange: "#d28a52", red:   "#c6523f",
};

function applyTheme(isDark, shellTh) {
  const r = document.documentElement;
  if (shellTh) {
    r.style.setProperty("--bg-from", shellTh.appBg);
    r.style.setProperty("--bg-to", shellTh.appBg);
    r.style.setProperty("--card-bg", shellTh.paper);
    r.style.setProperty("--card-bd", shellTh.line);
    r.style.setProperty("--text-hi", shellTh.ink);
    r.style.setProperty("--text", shellTh.ink2 || shellTh.ink);
    r.style.setProperty("--muted", shellTh.muted);
    r.style.setProperty("--sep", shellTh.line2 || shellTh.line);
    r.style.setProperty("--pill-bg", shellTh.paperAlt);
    r.style.setProperty("--pill-act", "rgba(181,154,91,0.22)");
    r.style.setProperty("--row-alt", shellTh.paperAlt);
    r.style.setProperty("--row-hov", shellTh.rowH || "rgba(181,154,91,0.05)");
    r.style.setProperty("--nav-bg", shellTh.paperSolid || shellTh.paper);
    r.style.setProperty("--tip-bg", shellTh.paperSolid || shellTh.paper);
    r.style.setProperty("--mono", "'IBM Plex Mono','Fira Mono',monospace");
    r.style.setProperty("--serif", "'Cormorant Garamond',Georgia,serif");
    return;
  }
  r.style.setProperty("--bg-from",  isDark ? "#0b1220"                : "#f6f5f1");
  r.style.setProperty("--bg-to",    isDark ? "#0f172a"                : "#f0ede8");
  r.style.setProperty("--card-bg",  isDark ? "rgba(15,23,42,0.75)"    : "rgba(255,255,255,0.92)");
  r.style.setProperty("--card-bd",  isDark ? "rgba(148,163,184,0.13)" : "rgba(30,40,34,0.10)");
  r.style.setProperty("--text-hi",  isDark ? "#e2e8f0"                : "#1f2a24");
  r.style.setProperty("--text",     isDark ? "#cbd5e1"                : "#2d3a32");
  r.style.setProperty("--muted",    isDark ? "#94a3b8"                : "#6f7b74");
  r.style.setProperty("--sep",      isDark ? "rgba(148,163,184,0.11)" : "rgba(30,40,34,0.07)");
  r.style.setProperty("--pill-bg",  isDark ? "rgba(30,41,59,0.75)"    : "rgba(238,234,228,0.92)");
  r.style.setProperty("--pill-act", isDark ? "rgba(181,154,91,0.18)"  : "rgba(181,154,91,0.22)");
  r.style.setProperty("--row-alt",  isDark ? "rgba(15,23,42,0.40)"    : "rgba(248,246,242,0.55)");
  r.style.setProperty("--row-hov",  isDark ? "rgba(201,168,76,0.05)"  : "rgba(181,154,91,0.05)");
  r.style.setProperty("--nav-bg",   isDark ? "rgba(11,18,32,0.90)"    : "rgba(246,245,241,0.93)");
  r.style.setProperty("--tip-bg",   isDark ? "rgba(11,18,32,0.97)"    : "rgba(255,255,255,0.98)");
  r.style.setProperty("--mono",  "'IBM Plex Mono','Fira Mono',monospace");
  r.style.setProperty("--serif", "'Cormorant Garamond',Georgia,serif");
}

const signalTone  = s => ["BUY","ACCUMULATE","STRONG_BUY"].includes(s)?"pos":s==="SELL"||s==="AVOID"?"neg":"warn";
const scoreColor  = v => v>=75?C.green:v>=55?C.amber:C.red;
const mosColor    = v => v>=20?C.green:v>=5?C.amber:v>=-5?C.orange:C.red;
const cagrColor   = v => v>=15?C.green:v>=5?C.amber:C.red;
const ddColor     = v => v>-20?C.green:v>-35?C.amber:C.red;
const pctColor    = v => v>=0?C.green:C.red;
const erColor     = v => v>=3?C.green:v>=0?C.amber:C.red;  // Expected Return AQR
const evaColor    = v => v>=5?C.green:v>=0?C.amber:C.red; // ROIC-WACC points

const MOCK = [
  {
    symbol:"ONEGLOBAL.BO", company:"One Global Service Provider Ltd.", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:542.15, currency:"INR", ytd_pct:-25.66,
    iscore:75.9, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 27.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 64)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 94.3%)", dd_valuation_status:"yes",
    fund_score:91.7, fund_label:"Solide",
    fund_value_creation:92.5, fund_solvency:97.7,
    roic_vs_wacc:27.9, mos:94.3, fcf_yield:0, peg:0.02,
    valuation_status:"",
    tscore:64.0, tscore_label:"Momentum favorable (tscore 64)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:55.0, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.62, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8969, momentum_contribution:0.4163,
    value_carry_contribution:0.0016, tail_risk_penalty:0.05,
  },
  {
    symbol:"CMCX.L", company:"CMC Markets plc", sector:"Financials",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:347.0, currency:"GBp", ytd_pct:14.88,
    iscore:75.4, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 14.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 55)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 55.2%)", dd_valuation_status:"yes",
    fund_score:100.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:88.0,
    roic_vs_wacc:14.0, mos:55.2, fcf_yield:0, peg:0.74,
    valuation_status:"",
    tscore:55.0, tscore_label:"Momentum favorable (tscore 55)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:36.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:6.92, dist_style:"Dividend-led",
    dist_score:72.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.4, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8988, momentum_contribution:0.4201,
    value_carry_contribution:0.0013, tail_risk_penalty:0.15,
  },
  {
    symbol:"NICL.JK", company:"PT PAM Mineral Tbk", sector:"Materials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:850.0, currency:"IDR", ytd_pct:-33.33,
    iscore:75.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: BUY.",
    dd_marche_label:"Marche en transition - ULTRA_CONCENTRATED", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0H1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 39.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 29.3%)", dd_valuation_status:"yes",
    fund_score:89.7, fund_label:"Solide",
    fund_value_creation:88.6, fund_solvency:98.7,
    roic_vs_wacc:39.0, mos:29.3, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:65.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.96, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9007, momentum_contribution:0.5031,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"SMDR.JK", company:"PT Samudera Indonesia Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:342.0, currency:"IDR", ytd_pct:-11.73,
    iscore:74.8, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 2.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 58)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 1373.9%)", dd_valuation_status:"yes",
    fund_score:92.2, fund_label:"Solide",
    fund_value_creation:91.0, fund_solvency:93.0,
    roic_vs_wacc:2.8, mos:500, fcf_yield:0, peg:-0.16,
    valuation_status:"",
    tscore:58.0, tscore_label:"Momentum favorable (tscore 58)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:93.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:64.4, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.94, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8822, momentum_contribution:0.4672,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"BLTA.JK", company:"PT Berlian Laju Tanker Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:41.0, currency:"IDR", ytd_pct:2.38,
    iscore:74.6, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0D1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 1.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 57)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 97.9%)", dd_valuation_status:"yes",
    fund_score:89.3, fund_label:"Excellent",
    fund_value_creation:85.2, fund_solvency:81.3,
    roic_vs_wacc:1.0, mos:97.9, fcf_yield:0, peg:-0.16,
    valuation_status:"",
    tscore:57.0, tscore_label:"Momentum favorable (tscore 57)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:29.6, dist_sustainability:35.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.94, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8869, momentum_contribution:0.5224,
    value_carry_contribution:0.0016, tail_risk_penalty:0.15,
  },
  {
    symbol:"7191.T", company:"Entrust Inc.", sector:"Financials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"Japan",
    price:1096.0, currency:"JPY", ytd_pct:-9.79,
    iscore:74.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 25.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 54)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 63.9%)", dd_valuation_status:"yes",
    fund_score:97.5, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:85.0,
    roic_vs_wacc:25.0, mos:63.9, fcf_yield:0, peg:0.64,
    valuation_status:"",
    tscore:54.0, tscore_label:"Momentum favorable (tscore 54)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:11.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:2.53, dist_style:"Dividend-led",
    dist_score:80.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.01, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8725, momentum_contribution:0.4983,
    value_carry_contribution:0.0014, tail_risk_penalty:0.12,
  },
  {
    symbol:"NELY.JK", company:"PT Pelayaran Nelly Dwi Putri Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Wide", region:"Southeast Asia",
    country:"Indonesia",
    price:332.0, currency:"IDR", ytd_pct:-15.27,
    iscore:74.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 46)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 71.4%)", dd_valuation_status:"yes",
    fund_score:92.2, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:99.2,
    roic_vs_wacc:17.8, mos:71.4, fcf_yield:0, peg:0.83,
    valuation_status:"",
    tscore:46.0, tscore_label:"Momentum en transition (tscore 46)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:51.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:68.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-1.51, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8961, momentum_contribution:0.3926,
    value_carry_contribution:0.0014, tail_risk_penalty:0.12,
  },
  {
    symbol:"KAP.IL", company:"JSC National Atomic Company Kazatomprom", sector:"Energy",
    cap_class:"Mega Cap", moat:"Narrow", region:"Central Asia",
    country:"Kazakhstan",
    price:80.3, currency:"USD", ytd_pct:39.78,
    iscore:74.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 18.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 63.6%)", dd_valuation_status:"yes",
    fund_score:97.6, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:90.7,
    roic_vs_wacc:18.7, mos:63.6, fcf_yield:0, peg:0.05,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:48.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:72.8, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.64, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8489, momentum_contribution:0.3937,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"0ZQ.F", company:"JSC National Atomic Company Kazatomprom", sector:"Energy",
    cap_class:"Mega Cap", moat:"Narrow", region:"Central Asia",
    country:"Kazakhstan",
    price:69.8, currency:"EUR", ytd_pct:47.32,
    iscore:74.1, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 59)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 44.9%)", dd_valuation_status:"yes",
    fund_score:97.6, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:90.7,
    roic_vs_wacc:17.2, mos:44.9, fcf_yield:0, peg:0.05,
    valuation_status:"",
    tscore:59.0, tscore_label:"Momentum favorable (tscore 59)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:50.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:72.8, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.57, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8489, momentum_contribution:0.3891,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"4491.T", company:"Computer Management Co.", sector:"TSE",
    cap_class:"Mid Cap", moat:"Stalwarts", region:"Asia",
    country:"Japan",
    price:0, currency:"2943.000000", ytd_pct:-2.61,
    iscore:74.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 7.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 53)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 51.4%)", dd_valuation_status:"yes",
    fund_score:82.2, fund_label:"Fragile",
    fund_value_creation:79.7, fund_solvency:97.6,
    roic_vs_wacc:7.2, mos:51.4, fcf_yield:0, peg:-2.58,
    valuation_status:"",
    tscore:53.0, tscore_label:"Momentum favorable (tscore 53)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:18.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:2.54, dist_style:"Dividend-led",
    dist_score:72.3, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.37, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.7916, momentum_contribution:0.458,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"300724.SZ", company:"Shenzhen S.C New Energy Technology Corporation", sector:"Industrials",
    cap_class:"Mid Cap", moat:"Narrow", region:"Asia",
    country:"China",
    price:103.42, currency:"CNY", ytd_pct:10.19,
    iscore:73.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 21.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 106.3%)", dd_valuation_status:"yes",
    fund_score:64.9, fund_label:"Satisfaisant",
    fund_value_creation:0, fund_solvency:88.0,
    roic_vs_wacc:21.2, mos:106.3, fcf_yield:0, peg:0.11,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:14.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:2.08, dist_style:"Dividend-led",
    dist_score:63.8, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.94, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8051, momentum_contribution:0.5034,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"ILYDA.AT", company:"Ilyda SA", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Wide", region:"Europe",
    country:"Greece",
    price:4.08, currency:"EUR", ytd_pct:-20.63,
    iscore:73.3, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 23.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 61)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 21.1%)", dd_valuation_status:"yes",
    fund_score:98.9, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:87.8,
    roic_vs_wacc:23.6, mos:21.1, fcf_yield:0, peg:0.02,
    valuation_status:"",
    tscore:61.0, tscore_label:"Momentum favorable (tscore 61)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:39.6, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.97, dist_style:"Balanced",
    dist_score:54.3, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.62, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8687, momentum_contribution:0.394,
    value_carry_contribution:0.001, tail_risk_penalty:0.15,
  },
  {
    symbol:"8512.HK", company:"Hyfusin Group Holdings Limited", sector:"Consumer Staples",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"Hong Kong",
    price:0.325, currency:"HKD", ytd_pct:-4.48,
    iscore:73.1, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0D1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 39)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 79.6%)", dd_valuation_status:"yes",
    fund_score:93.5, fund_label:"Solide",
    fund_value_creation:92.0, fund_solvency:98.4,
    roic_vs_wacc:17.0, mos:79.6, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:39.0, tscore_label:"Momentum en transition (tscore 39)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:36.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:12.82, dist_style:"Buyback-led",
    dist_score:63.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.15, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.9078, momentum_contribution:0.2974,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"S56.SI", company:"Samudera Shipping Line Ltd", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Singapore",
    price:1.02, currency:"SGD", ytd_pct:-15.0,
    iscore:73.1, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 3.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 75.5%)", dd_valuation_status:"yes",
    fund_score:94.0, fund_label:"Solide",
    fund_value_creation:88.1, fund_solvency:96.0,
    roic_vs_wacc:3.1, mos:75.5, fcf_yield:0, peg:-0.14,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:94.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:71.3, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.66, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9335, momentum_contribution:0.348,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"LOGO.IS", company:"Logo Yazilim Sanayi ve Ticaret A.S.", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Narrow", region:"Europe",
    country:"Turkey",
    price:134.2, currency:"TRY", ytd_pct:-12.43,
    iscore:72.9, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 18.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 49)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 56.3%)", dd_valuation_status:"yes",
    fund_score:93.0, fund_label:"Fragile",
    fund_value_creation:92.5, fund_solvency:96.6,
    roic_vs_wacc:18.6, mos:56.3, fcf_yield:0, peg:0.19,
    valuation_status:"",
    tscore:49.0, tscore_label:"Momentum en transition (tscore 49)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:58.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:5.5, dist_style:"Dividend-led",
    dist_score:53.5, dist_sustainability:67.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:2.36, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9284, momentum_contribution:0.3398,
    value_carry_contribution:0.0013, tail_risk_penalty:0.08,
  },
  {
    symbol:"300751.SZ", company:"Suzhou Maxwell Technologies Co.", sector:"SZSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"220.010000", ytd_pct:-21.63,
    iscore:72.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0J a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 3.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 66)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 123.7%)", dd_valuation_status:"yes",
    fund_score:71.2, fund_label:"Satisfaisant",
    fund_value_creation:0, fund_solvency:89.0,
    roic_vs_wacc:3.1, mos:123.7, fcf_yield:0, peg:34.73,
    valuation_status:"",
    tscore:66.0, tscore_label:"Momentum favorable (tscore 66)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:37.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.74, dist_style:"Dividend-led",
    dist_score:66.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.74, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.6767, momentum_contribution:0.5532,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"MBSS.JK", company:"PT Mitrabahtera Segara Sejati Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:1515.0, currency:"IDR", ytd_pct:-51.12,
    iscore:72.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (Narrow moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 64561.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 57)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 87.3%)", dd_valuation_status:"yes",
    fund_score:86.2, fund_label:"Excellent",
    fund_value_creation:52.0, fund_solvency:94.0,
    roic_vs_wacc:645.6, mos:87.3, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:57.0, tscore_label:"Momentum favorable (tscore 57)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:16.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:50.8, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.56, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8745, momentum_contribution:0.4219,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"ELPI.JK", company:"PT Pelayaran Nasional Ekalya Purnamasari Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:1910.0, currency:"IDR", ytd_pct:141.88,
    iscore:72.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 4.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 66)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Valorisation proche valeur juste (MOS -371.6%)", dd_valuation_status:"watch",
    fund_score:84.9, fund_label:"Excellent",
    fund_value_creation:83.7, fund_solvency:98.9,
    roic_vs_wacc:4.5, mos:-300, fcf_yield:0, peg:0.17,
    valuation_status:"",
    tscore:66.0, tscore_label:"Momentum favorable (tscore 66)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:51.9, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.54, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et la qualité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8557, momentum_contribution:0.3856,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"IBINFO.BO", company:"IB Infotech Enterprises Limited", sector:"Consumer Staples",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:594.1, currency:"INR", ytd_pct:114.03,
    iscore:72.4, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0A1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 22.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 71.9%)", dd_valuation_status:"yes",
    fund_score:87.9, fund_label:"Excellent",
    fund_value_creation:79.8, fund_solvency:94.0,
    roic_vs_wacc:22.1, mos:71.9, fcf_yield:0, peg:-24.06,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:64.6, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:48.7, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.86, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8984, momentum_contribution:0.4274,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"TSFA.F", company:"Taiwan Semiconductor Manufacturing Company Limited", sector:"Information Technology",
    cap_class:"Mega Cap", moat:"Wide", region:"Asia",
    country:"Germany",
    price:294.0, currency:"EUR", ytd_pct:13.2,
    iscore:72.4, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 14.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 58)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 93.8%)", dd_valuation_status:"yes",
    fund_score:94.3, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:97.7,
    roic_vs_wacc:14.6, mos:93.8, fcf_yield:0, peg:0.77,
    valuation_status:"",
    tscore:58.0, tscore_label:"Momentum favorable (tscore 58)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:34.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.08, dist_style:"Dividend-led",
    dist_score:68.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:2.33, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9103, momentum_contribution:0.4151,
    value_carry_contribution:0.0009, tail_risk_penalty:0.08,
  },
  {
    symbol:"603986.SS", company:"GigaDevice Semiconductor (Beijing) Inc.", sector:"Information Technology",
    cap_class:"Mid Cap", moat:"Narrow", region:"Asia",
    country:"China",
    price:249.1, currency:"CNY", ytd_pct:11.13,
    iscore:72.4, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. EVA negative (ROIC-WACC -4.7 pts). Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Destruction de valeur (ROIC-WACC -4.7 pts)", dd_value_status:"no",
    dd_momentum_label:"Momentum favorable (tscore 61)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 27.2%)", dd_valuation_status:"yes",
    fund_score:100.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:83.9,
    roic_vs_wacc:-4.7, mos:27.2, fcf_yield:0, peg:0.11,
    valuation_status:"",
    tscore:61.0, tscore_label:"Momentum favorable (tscore 61)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:3.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.46, dist_style:"Buyback-led",
    dist_score:53.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-1.98, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8861, momentum_contribution:0.4181,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"SIMO", company:"Silicon Motion Technology Corporation", sector:"Information Technology",
    cap_class:"Small Cap", moat:"Narrow", region:"Asia",
    country:"USA",
    price:113.9, currency:"USD", ytd_pct:21.13,
    iscore:72.3, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 1.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 44.9%)", dd_valuation_status:"yes",
    fund_score:91.8, fund_label:"Excellent",
    fund_value_creation:90.5, fund_solvency:98.4,
    roic_vs_wacc:1.8, mos:44.9, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:15.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:85.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.1, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8528, momentum_contribution:0.3801,
    value_carry_contribution:0.0013, tail_risk_penalty:0.12,
  },
  {
    symbol:"TSM", company:"Taiwan Semiconductor Manufacturing Company Limited", sector:"Information Technology",
    cap_class:"Mega Cap", moat:"Wide", region:"Asia",
    country:"USA",
    price:339.04, currency:"USD", ytd_pct:11.21,
    iscore:72.3, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 14.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 59)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 92.7%)", dd_valuation_status:"yes",
    fund_score:94.2, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:97.7,
    roic_vs_wacc:14.8, mos:92.7, fcf_yield:0, peg:0.81,
    valuation_status:"",
    tscore:59.0, tscore_label:"Momentum favorable (tscore 59)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:33.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.09, dist_style:"Dividend-led",
    dist_score:69.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-1.95, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9084, momentum_contribution:0.4245,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"LRCX.BA", company:"Lam Research Corporation", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Wide", region:"North America",
    country:"Argentina",
    price:5865.0, currency:"ARS", ytd_pct:18.31,
    iscore:72.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 21.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 68)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Valorisation proche valeur juste (MOS -8085.8%)", dd_valuation_status:"watch",
    fund_score:97.7, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:81.5,
    roic_vs_wacc:21.9, mos:-300, fcf_yield:0, peg:-0.01,
    valuation_status:"",
    tscore:68.0, tscore_label:"Momentum favorable (tscore 68)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.32, dist_style:"Buyback-led",
    dist_score:73.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.53, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8751, momentum_contribution:0.5081,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"ADES.JK", company:"PT Akasha Wira International Tbk", sector:"Consumer Staples",
    cap_class:"Small Cap", moat:"Wide", region:"Southeast Asia",
    country:"Indonesia",
    price:16325.0, currency:"IDR", ytd_pct:7.13,
    iscore:72.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 22.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 57)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 64.9%)", dd_valuation_status:"yes",
    fund_score:99.8, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:85.8,
    roic_vs_wacc:22.0, mos:64.9, fcf_yield:0, peg:0.31,
    valuation_status:"",
    tscore:57.0, tscore_label:"Momentum favorable (tscore 57)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:54.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:54.0, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-3.03, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et la qualité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.927, momentum_contribution:0.4375,
    value_carry_contribution:0.0014, tail_risk_penalty:0.12,
  },
  {
    symbol:"AZN", company:"AstraZeneca PLC", sector:"Health Care",
    cap_class:"Mega Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:203.49, currency:"USD", ytd_pct:114.53,
    iscore:72.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH3a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 8.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 66)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 21.4%)", dd_valuation_status:"yes",
    fund_score:82.0, fund_label:"Excellent",
    fund_value_creation:82.5, fund_solvency:80.0,
    roic_vs_wacc:8.0, mos:21.4, fcf_yield:0, peg:-0.71,
    valuation_status:"",
    tscore:66.0, tscore_label:"Momentum favorable (tscore 66)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:18.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:2.26, dist_style:"Dividend-led",
    dist_score:71.7, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.83, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7918, momentum_contribution:0.406,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"RCL.BK", company:"Regional Container Lines Public Company Limited", sector:"Industrials",
    cap_class:"Small Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Thailand",
    price:30.75, currency:"THB", ytd_pct:15.6,
    iscore:72.1, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 9.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 41)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 35.7%)", dd_valuation_status:"yes",
    fund_score:92.7, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:98.1,
    roic_vs_wacc:9.8, mos:35.7, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:41.0, tscore_label:"Momentum en transition (tscore 41)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:102.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:59.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.07, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9196, momentum_contribution:0.2759,
    value_carry_contribution:0.0014, tail_risk_penalty:0.15,
  },
  {
    symbol:"SCZ.V", company:"Santacruz Silver Mining Ltd.", sector:"Materials",
    cap_class:"Nano Cap", moat:"None", region:"North America",
    country:"Canada",
    price:10.88, currency:"CAD", ytd_pct:-8.62,
    iscore:72.0, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"warn", dd_global_score:64.7,
    dd_yes:3, dd_no:2, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. EVA negative (ROIC-WACC -4.3 pts). Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (None moat / signal BUY)", dd_moat_status:"no",
    dd_value_label:"Destruction de valeur (ROIC-WACC -4.3 pts)", dd_value_status:"no",
    dd_momentum_label:"Momentum favorable (tscore 68)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 338.8%)", dd_valuation_status:"yes",
    fund_score:43.9, fund_label:"Solide",
    fund_value_creation:0, fund_solvency:48.4,
    roic_vs_wacc:-4.3, mos:338.8, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:68.0, tscore_label:"Momentum favorable (tscore 68)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:11.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:54.0, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.2, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.6505, momentum_contribution:0.5179,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"AIY.SI", company:"iFAST Corporation Ltd.", sector:"Information Technology",
    cap_class:"Small Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Singapore",
    price:8.86, currency:"SGD", ytd_pct:-5.67,
    iscore:72.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 11.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 48)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 21.2%)", dd_valuation_status:"yes",
    fund_score:88.6, fund_label:"Solide",
    fund_value_creation:76.9, fund_solvency:94.5,
    roic_vs_wacc:11.7, mos:21.2, fcf_yield:0, peg:0.25,
    valuation_status:"",
    tscore:48.0, tscore_label:"Momentum en transition (tscore 48)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:54.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.88, dist_style:"Dividend-led",
    dist_score:62.7, dist_sustainability:67.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.96, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8652, momentum_contribution:0.4,
    value_carry_contribution:0.001, tail_risk_penalty:0.08,
  },
  {
    symbol:"SUS.L", company:"S&U plc", sector:"Financials",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:2020.0, currency:"GBp", ytd_pct:-8.65,
    iscore:72.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 7.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 58)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 73.1%)", dd_valuation_status:"yes",
    fund_score:80.9, fund_label:"Solide",
    fund_value_creation:79.4, fund_solvency:81.7,
    roic_vs_wacc:7.2, mos:73.1, fcf_yield:0, peg:-0.56,
    valuation_status:"",
    tscore:58.0, tscore_label:"Momentum favorable (tscore 58)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:21.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:54.8, dist_sustainability:60.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:6.56, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est modérée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8243, momentum_contribution:0.5401,
    value_carry_contribution:0.0015, tail_risk_penalty:0.03,
  },
  {
    symbol:"NXT", company:"Nextracker Inc.", sector:"Energy",
    cap_class:"Mid Cap", moat:"None", region:"North America",
    country:"USA",
    price:113.08, currency:"USD", ytd_pct:38.39,
    iscore:71.8, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (None moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 22.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 62)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 61.6%)", dd_valuation_status:"yes",
    fund_score:83.8, fund_label:"Satisfaisant",
    fund_value_creation:76.3, fund_solvency:95.2,
    roic_vs_wacc:22.5, mos:61.6, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:62.0, tscore_label:"Momentum favorable (tscore 62)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:25.49, dist_style:"Buyback-led",
    dist_score:74.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.68, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.851, momentum_contribution:0.4689,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"4686.T", company:"JustSystems Corporation", sector:"Information Technology",
    cap_class:"Small Cap", moat:"Wide", region:"Asia",
    country:"Japan",
    price:3530.0, currency:"JPY", ytd_pct:-31.03,
    iscore:71.6, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Marche en transition - ULTRA_CONCENTRATED", dd_marche_status:"watch",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 9.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 49)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 26.3%)", dd_valuation_status:"yes",
    fund_score:93.5, fund_label:"Excellent",
    fund_value_creation:84.3, fund_solvency:98.5,
    roic_vs_wacc:9.3, mos:26.3, fcf_yield:0, peg:-1.05,
    valuation_status:"",
    tscore:49.0, tscore_label:"Momentum en transition (tscore 49)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:-7.9, bt_5y_underperf:true, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:62.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.0, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9531, momentum_contribution:0.3858,
    value_carry_contribution:0.001, tail_risk_penalty:0.15,
  },
  {
    symbol:"SBS", company:"Companhia de Saneamento Básico do Estado de São Paulo - SABESP", sector:"Utilities",
    cap_class:"Large Cap", moat:"Narrow", region:"Latin America",
    country:"USA",
    price:30.39, currency:"USD", ytd_pct:27.92,
    iscore:71.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 16.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 64.3%)", dd_valuation_status:"yes",
    fund_score:88.4, fund_label:"Excellent",
    fund_value_creation:77.1, fund_solvency:86.6,
    roic_vs_wacc:16.9, mos:64.3, fcf_yield:0, peg:0.04,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:8.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:62.4, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.53, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9081, momentum_contribution:0.4204,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"NVDA", company:"NVIDIA Corporation", sector:"Information Technology",
    cap_class:"Mega Cap", moat:"Wide", region:"North America",
    country:"USA",
    price:177.39, currency:"USD", ytd_pct:-6.49,
    iscore:71.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 44.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 54)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Dynamique)", dd_rr_status:"yes",
    dd_valuation_label:"Valorisation tendue (MOS -26.2%)", dd_valuation_status:"no",
    fund_score:100.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:88.0,
    roic_vs_wacc:44.9, mos:-26.2, fcf_yield:0, peg:0.09,
    valuation_status:"",
    tscore:54.0, tscore_label:"Momentum en transition (tscore 54)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:90.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.64, dist_style:"Buyback-led",
    dist_score:70.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.39, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9232, momentum_contribution:0.3741,
    value_carry_contribution:0.0011, tail_risk_penalty:0.12,
  },
  {
    symbol:"6580.T", company:"Writeup Co.", sector:"TSE",
    cap_class:"Mid Cap", moat:"Asset Plays", region:"Asia",
    country:"Japan",
    price:0, currency:"1140.000000", ytd_pct:-43.07,
    iscore:71.5, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - CONCENTRATED", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 51)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 66.1%)", dd_valuation_status:"yes",
    fund_score:91.5, fund_label:"Excellent",
    fund_value_creation:79.8, fund_solvency:98.8,
    roic_vs_wacc:12.9, mos:66.1, fcf_yield:0, peg:0.07,
    valuation_status:"",
    tscore:51.0, tscore_label:"Momentum en transition (tscore 51)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:12.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.32, dist_style:"Buyback-led",
    dist_score:64.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.45, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et la qualité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9408, momentum_contribution:0.4022,
    value_carry_contribution:0.0013, tail_risk_penalty:0.15,
  },
  {
    symbol:"KESAR.BO", company:"Kesar India Ltd.", sector:"Real Estate",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:1159.7, currency:"INR", ytd_pct:-9.95,
    iscore:71.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: BUY.",
    dd_marche_label:"Marche en transition - MODERATE", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0G a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 60.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 64)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 3.5%)", dd_valuation_status:"yes",
    fund_score:92.3, fund_label:"Solide",
    fund_value_creation:86.1, fund_solvency:89.7,
    roic_vs_wacc:60.4, mos:3.5, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:64.0, tscore_label:"Momentum favorable (tscore 64)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:54.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.48, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8857, momentum_contribution:0.5466,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"BRISA.IS", company:"Brisa Bridgestone Sabanci Lastik Sanayi ve Ticaret A.S.", sector:"Consumer Discretionary",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"Turkey",
    price:81.0, currency:"TRY", ytd_pct:-3.37,
    iscore:71.3, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 2.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 47)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 63.7%)", dd_valuation_status:"yes",
    fund_score:87.5, fund_label:"Excellent",
    fund_value_creation:88.0, fund_solvency:73.7,
    roic_vs_wacc:2.6, mos:63.7, fcf_yield:0, peg:-0.3,
    valuation_status:"",
    tscore:47.0, tscore_label:"Momentum en transition (tscore 47)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:73.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:42.5, dist_sustainability:55.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.81, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8344, momentum_contribution:0.321,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"SJS.BO", company:"S.J.S. Enterprises Limited", sector:"Consumer Discretionary",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:1603.35, currency:"INR", ytd_pct:-8.94,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Valorisation proche valeur juste (MOS -210.7%)", dd_valuation_status:"watch",
    fund_score:92.8, fund_label:"Excellent",
    fund_value_creation:89.6, fund_solvency:98.7,
    roic_vs_wacc:12.8, mos:-210.7, fcf_yield:0, peg:0.94,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.59, dist_style:"Buyback-led",
    dist_score:62.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.6, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8939, momentum_contribution:0.4607,
    value_carry_contribution:0.0015, tail_risk_penalty:0.05,
  },
  {
    symbol:"SJS.NS", company:"S.J.S. Enterprises Limited", sector:"Consumer Discretionary",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:1600.0, currency:"INR", ytd_pct:-8.74,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Valorisation proche valeur juste (MOS -210.0%)", dd_valuation_status:"watch",
    fund_score:92.8, fund_label:"Excellent",
    fund_value_creation:89.6, fund_solvency:98.7,
    roic_vs_wacc:12.8, mos:-210.0, fcf_yield:0, peg:0.94,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.59, dist_style:"Buyback-led",
    dist_score:62.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.03, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8939, momentum_contribution:0.4609,
    value_carry_contribution:0.0015, tail_risk_penalty:0.05,
  },
  {
    symbol:"ASR", company:"Grupo Aeroportuario del Sureste", sector:"NYSE",
    cap_class:"Large Cap", moat:"Cyclicals", region:"Latin America",
    country:"Mexico",
    price:0, currency:"347.300000", ytd_pct:-6.54,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 51)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 88.4%)", dd_valuation_status:"yes",
    fund_score:100.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:82.1,
    roic_vs_wacc:17.3, mos:88.4, fcf_yield:0, peg:0.36,
    valuation_status:"",
    tscore:51.0, tscore_label:"Momentum en transition (tscore 51)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:21.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:72.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.45, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.96, momentum_contribution:0.4401,
    value_carry_contribution:0.0015, tail_risk_penalty:0.05,
  },
  {
    symbol:"002371.SZ", company:"NAURA Technology Group Co.", sector:"SZSE",
    cap_class:"Mega Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"426.450000", ytd_pct:-5.27,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (Narrow moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 10.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 52)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 77.8%)", dd_valuation_status:"yes",
    fund_score:80.1, fund_label:"Solide",
    fund_value_creation:79.1, fund_solvency:96.4,
    roic_vs_wacc:10.5, mos:77.8, fcf_yield:0, peg:0.86,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum en transition (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:35.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.28, dist_style:"Dividend-led",
    dist_score:71.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.1, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.7213, momentum_contribution:0.4183,
    value_carry_contribution:0.0015, tail_risk_penalty:0.08,
  },
  {
    symbol:"FRP.L", company:"FRP Advisory Group plc", sector:"Financials",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:114.0, currency:"GBp", ytd_pct:-18.28,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Momentum degrade (tscore 41) - attendre signal. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 23.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum defavorable - Signal faible · éviter ou alléger", dd_momentum_status:"no",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 67.2%)", dd_valuation_status:"yes",
    fund_score:91.6, fund_label:"Solide",
    fund_value_creation:82.0, fund_solvency:97.2,
    roic_vs_wacc:23.4, mos:67.2, fcf_yield:0, peg:0.2,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum defavorable - Signal faible · éviter ou alléger",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:21.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:90.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.05, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8847, momentum_contribution:0.3024,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"OSIAJEE.BO", company:"Osiajee Texfab Limited", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Asset Plays", region:"Asia",
    country:"India",
    price:420.0, currency:"INR", ytd_pct:8.66,
    iscore:71.0, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 24.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 65)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 6236.4%)", dd_valuation_status:"yes",
    fund_score:80.8, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:98.4,
    roic_vs_wacc:24.9, mos:500, fcf_yield:0, peg:0.71,
    valuation_status:"",
    tscore:65.0, tscore_label:"Momentum favorable (tscore 65)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:7.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:54.8, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:0.3, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8136, momentum_contribution:0.4459,
    value_carry_contribution:0.0009, tail_risk_penalty:0.08,
  },
  {
    symbol:"HRUM.JK", company:"PT Harum Energy Tbk", sector:"Energy",
    cap_class:"Nano Cap", moat:"Wide", region:"Southeast Asia",
    country:"Indonesia",
    price:995.0, currency:"IDR", ytd_pct:-5.12,
    iscore:70.9, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:92.0,
    dd_yes:5, dd_no:0, dd_watch:1,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 5.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 52)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 103.7%)", dd_valuation_status:"yes",
    fund_score:93.9, fund_label:"Excellent",
    fund_value_creation:90.6, fund_solvency:98.6,
    roic_vs_wacc:5.7, mos:103.7, fcf_yield:0, peg:-0.25,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum favorable (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:50.6, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:4.68, dist_style:"Dividend-led",
    dist_score:67.8, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.47, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9419, momentum_contribution:0.4447,
    value_carry_contribution:0.0016, tail_risk_penalty:0.15,
  },
  {
    symbol:"JTPE.JK", company:"PT Jasuindo Tiga Perkasa Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:570.0, currency:"IDR", ytd_pct:4.35,
    iscore:70.9, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 65)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 6.8%)", dd_valuation_status:"yes",
    fund_score:89.2, fund_label:"Solide",
    fund_value_creation:91.3, fund_solvency:98.0,
    roic_vs_wacc:17.6, mos:6.8, fcf_yield:0, peg:0.43,
    valuation_status:"",
    tscore:65.0, tscore_label:"Momentum favorable (tscore 65)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:35.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:73.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:2.17, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8673, momentum_contribution:0.4398,
    value_carry_contribution:0.0011, tail_risk_penalty:0.08,
  },
  {
    symbol:"THX.L", company:"Thor Explorations Ltd.", sector:"Materials",
    cap_class:"Nano Cap", moat:"Narrow", region:"North America",
    country:"UK",
    price:76.5, currency:"GBp", ytd_pct:2.86,
    iscore:70.9, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: BUY.",
    dd_marche_label:"Marche en transition - MODERATE", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 35.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 64)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 46.9%)", dd_valuation_status:"yes",
    fund_score:79.3, fund_label:"Satisfaisant",
    fund_value_creation:84.5, fund_solvency:76.3,
    roic_vs_wacc:35.1, mos:46.9, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:64.0, tscore_label:"Momentum favorable (tscore 64)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:74.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.09, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7437, momentum_contribution:0.3875,
    value_carry_contribution:0.0012, tail_risk_penalty:0.05,
  },
  {
    symbol:"ARASE.IS", company:"Dogu Aras Enerji Yatirimlari AS", sector:"Utilities",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"Turkey",
    price:93.7, currency:"TRY", ytd_pct:18.83,
    iscore:70.8, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH4a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 29.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 61)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 65.3%)", dd_valuation_status:"yes",
    fund_score:83.1, fund_label:"Excellent",
    fund_value_creation:79.4, fund_solvency:97.0,
    roic_vs_wacc:29.3, mos:65.3, fcf_yield:0, peg:-0.92,
    valuation_status:"",
    tscore:61.0, tscore_label:"Momentum favorable (tscore 61)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:53.8, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.09, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8306, momentum_contribution:0.4299,
    value_carry_contribution:0.0016, tail_risk_penalty:0.05,
  },
  {
    symbol:"300502.SZ", company:"Eoptolink Technology Inc.", sector:"SZSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"455.300000", ytd_pct:23.06,
    iscore:70.8, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0D1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 30.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 67)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 3231.4%)", dd_valuation_status:"yes",
    fund_score:87.8, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:98.8,
    roic_vs_wacc:30.8, mos:500, fcf_yield:0, peg:0.09,
    valuation_status:"",
    tscore:67.0, tscore_label:"Momentum favorable (tscore 67)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:52.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.15, dist_style:"Dividend-led",
    dist_score:71.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.4, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7924, momentum_contribution:0.4723,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"GAPB.MX", company:"Grupo Aeroportuario del Pacífico", sector:"BMV",
    cap_class:"Large Cap", moat:"Cyclicals", region:"Latin America",
    country:"Mexico",
    price:0, currency:"441.030000", ytd_pct:-1.9,
    iscore:70.8, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 18.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 53)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 65.3%)", dd_valuation_status:"yes",
    fund_score:91.0, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:71.6,
    roic_vs_wacc:18.8, mos:65.3, fcf_yield:0, peg:-2.19,
    valuation_status:"",
    tscore:53.0, tscore_label:"Momentum en transition (tscore 53)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:10.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:53.7, dist_sustainability:70.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.99, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8969, momentum_contribution:0.4397,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"WIIM.JK", company:"PT Wismilak Inti Makmur Tbk", sector:"Consumer Staples",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:1805.0, currency:"IDR", ytd_pct:15.15,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 11.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 65)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 57.8%)", dd_valuation_status:"yes",
    fund_score:84.3, fund_label:"Solide",
    fund_value_creation:85.6, fund_solvency:98.2,
    roic_vs_wacc:11.7, mos:57.8, fcf_yield:0, peg:-0.12,
    valuation_status:"",
    tscore:65.0, tscore_label:"Momentum favorable (tscore 65)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:65.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:69.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.33, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7866, momentum_contribution:0.5539,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"MRK", company:"Merck & Co.", sector:"NYSE",
    cap_class:"Mega Cap", moat:"Asset Plays", region:"North America",
    country:"USA",
    price:0, currency:"120.870000", ytd_pct:-2.85,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH3b a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 57)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 95.7%)", dd_valuation_status:"yes",
    fund_score:88.8, fund_label:"Excellent",
    fund_value_creation:87.3, fund_solvency:82.7,
    roic_vs_wacc:17.8, mos:95.7, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:57.0, tscore_label:"Momentum favorable (tscore 57)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:17.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:3.62, dist_style:"Dividend-led",
    dist_score:85.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.65, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8715, momentum_contribution:0.4473,
    value_carry_contribution:0.0011, tail_risk_penalty:0.05,
  },
  {
    symbol:"SKRN.JK", company:"PT Superkrane Mitra Utama Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:432.0, currency:"IDR", ytd_pct:-19.27,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 90.7%)", dd_valuation_status:"yes",
    fund_score:84.5, fund_label:"Excellent",
    fund_value_creation:85.4, fund_solvency:81.9,
    roic_vs_wacc:17.6, mos:90.7, fcf_yield:0, peg:-0.37,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:47.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:64.7, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.05, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8185, momentum_contribution:0.4939,
    value_carry_contribution:0.0016, tail_risk_penalty:0.08,
  },
  {
    symbol:"290650.KQ", company:"L&C Bio Co.", sector:"KOSDAQ",
    cap_class:"Mega Cap", moat:"Asset Plays", region:"Asia",
    country:"Korea",
    price:0, currency:"65900.000000", ytd_pct:-31.74,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. EVA negative (ROIC-WACC -10.5 pts). Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0J a surveiller", dd_moat_status:"watch",
    dd_value_label:"Destruction de valeur (ROIC-WACC -10.5 pts)", dd_value_status:"no",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 2369.3%)", dd_valuation_status:"yes",
    fund_score:94.5, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:51.9,
    roic_vs_wacc:-10.5, mos:500, fcf_yield:0, peg:0.02,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:25.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:68.8, dist_sustainability:55.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.61, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.7114, momentum_contribution:0.4502,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"HY9H.F", company:"SK hynix Inc.", sector:"Information Technology",
    cap_class:"Mega Cap", moat:"Narrow", region:"Asia",
    country:"Germany",
    price:502.0, currency:"EUR", ytd_pct:33.68,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 68)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 99.8%)", dd_valuation_status:"yes",
    fund_score:83.6, fund_label:"Solide",
    fund_value_creation:81.2, fund_solvency:94.5,
    roic_vs_wacc:12.8, mos:99.8, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:68.0, tscore_label:"Momentum favorable (tscore 68)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:52.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.75, dist_style:"Dividend-led",
    dist_score:65.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.33, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7596, momentum_contribution:0.4587,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"VOT.WA", company:"Votum S.A.", sector:"Industrials",
    cap_class:"Small Cap", moat:"Wide", region:"Europe",
    country:"Poland",
    price:42.1, currency:"PLN", ytd_pct:-8.88,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 23.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 84.7%)", dd_valuation_status:"yes",
    fund_score:88.7, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:97.3,
    roic_vs_wacc:23.5, mos:84.7, fcf_yield:0, peg:-0.23,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:34.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:75.8, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.01, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8778, momentum_contribution:0.4504,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"UMC", company:"United Microelectronics Corporation", sector:"Information Technology",
    cap_class:"Mega Cap", moat:"Narrow", region:"Asia",
    country:"Taiwan",
    price:8.67, currency:"USD", ytd_pct:14.25,
    iscore:70.7, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 2.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 55)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 98.7%)", dd_valuation_status:"yes",
    fund_score:92.1, fund_label:"Excellent",
    fund_value_creation:91.7, fund_solvency:97.8,
    roic_vs_wacc:2.5, mos:98.7, fcf_yield:0, peg:-0.53,
    valuation_status:"",
    tscore:55.0, tscore_label:"Momentum favorable (tscore 55)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:46.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:88.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.66, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8727, momentum_contribution:0.315,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"000733.SZ", company:"China Zhenhua (Group) Science & Technology Co.", sector:"SZSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"44.000000", ytd_pct:-21.39,
    iscore:70.6, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 2.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 49)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 63.9%)", dd_valuation_status:"yes",
    fund_score:97.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:76.2,
    roic_vs_wacc:2.1, mos:63.9, fcf_yield:0, peg:-0.37,
    valuation_status:"",
    tscore:49.0, tscore_label:"Momentum en transition (tscore 49)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:28.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:68.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.54, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8271, momentum_contribution:0.3897,
    value_carry_contribution:0.0014, tail_risk_penalty:0.15,
  },
  {
    symbol:"0LB2.L", company:"Supernus Pharmaceuticals", sector:"LSE",
    cap_class:"Small Cap", moat:"Asset Plays", region:"Europe",
    country:"UK",
    price:0, currency:"50.870000", ytd_pct:-10.65,
    iscore:70.6, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur mitigee (ROIC-WACC 0.2 pts)", dd_value_status:"watch",
    dd_momentum_label:"Momentum favorable (tscore 51)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 45.8%)", dd_valuation_status:"yes",
    fund_score:81.0, fund_label:"Solide",
    fund_value_creation:57.9, fund_solvency:95.7,
    roic_vs_wacc:0.2, mos:45.8, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:51.0, tscore_label:"Momentum favorable (tscore 51)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.75, dist_style:"Buyback-led",
    dist_score:61.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.3, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7972, momentum_contribution:0.4137,
    value_carry_contribution:0.0016, tail_risk_penalty:0.05,
  },
  {
    symbol:"GSI.V", company:"Gatekeeper Systems Inc.", sector:"Industrials",
    cap_class:"Nano Cap", moat:"None", region:"North America",
    country:"Canada",
    price:1.24, currency:"CAD", ytd_pct:-38.38,
    iscore:70.6, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"warn", dd_global_score:64.7,
    dd_yes:3, dd_no:2, dd_watch:1,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement defavorable - Cassure baissière majeure", dd_marche_status:"no",
    dd_moat_label:"Avantage concurrentiel limite (None moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 10.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 55)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 1380.0%)", dd_valuation_status:"yes",
    fund_score:96.1, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:81.8,
    roic_vs_wacc:10.7, mos:500, fcf_yield:0, peg:-0.79,
    valuation_status:"",
    tscore:55.0, tscore_label:"Momentum favorable (tscore 55)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:19.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.7, dist_style:"Buyback-led",
    dist_score:61.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.9, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7719, momentum_contribution:0.3974,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"1384.T", company:"Hokuryo Co.", sector:"TSE",
    cap_class:"Mid Cap", moat:"Fast Growers", region:"Asia",
    country:"Japan",
    price:0, currency:"2874.000000", ytd_pct:-12.74,
    iscore:70.6, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 6.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 49.4%)", dd_valuation_status:"yes",
    fund_score:84.0, fund_label:"Fragile",
    fund_value_creation:88.3, fund_solvency:98.0,
    roic_vs_wacc:6.1, mos:49.4, fcf_yield:0, peg:0.03,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:21.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:64.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.04, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8042, momentum_contribution:0.4571,
    value_carry_contribution:0.0014, tail_risk_penalty:0.12,
  },
  {
    symbol:"OMAB", company:"Grupo Aeroportuario del Centro Norte", sector:"NGS",
    cap_class:"Large Cap", moat:"Cyclicals", region:"Latin America",
    country:"Mexico",
    price:0, currency:"116.730000", ytd_pct:-6.08,
    iscore:70.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (Margin moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 27.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 87.9%)", dd_valuation_status:"yes",
    fund_score:91.2, fund_label:"Excellent",
    fund_value_creation:92.0, fund_solvency:88.4,
    roic_vs_wacc:27.0, mos:87.9, fcf_yield:0, peg:-8.28,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:24.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:62.8, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.33, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9261, momentum_contribution:0.3825,
    value_carry_contribution:0.0011, tail_risk_penalty:0.05,
  },
  {
    symbol:"2GB.DE", company:"2G Energy AG", sector:"Industrials",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"Germany",
    price:40.0, currency:"EUR", ytd_pct:7.37,
    iscore:70.5, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 9.9%)", dd_valuation_status:"yes",
    fund_score:82.7, fund_label:"Satisfaisant",
    fund_value_creation:92.5, fund_solvency:97.2,
    roic_vs_wacc:12.3, mos:9.9, fcf_yield:0, peg:0.54,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:26.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:64.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:0.28, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7261, momentum_contribution:0.3735,
    value_carry_contribution:0.001, tail_risk_penalty:0.08,
  },
  {
    symbol:"PROF.AT", company:"Profile Systems & Software SA", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Narrow", region:"Europe",
    country:"Greece",
    price:7.09, currency:"EUR", ytd_pct:-14.45,
    iscore:70.4, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: BUY.",
    dd_marche_label:"Marche en transition - CONCENTRATED", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle PH2b a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 13.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 24.6%)", dd_valuation_status:"yes",
    fund_score:83.9, fund_label:"Excellent",
    fund_value_creation:90.9, fund_solvency:97.4,
    roic_vs_wacc:13.2, mos:24.6, fcf_yield:0, peg:0.52,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:30.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.47, dist_style:"Dividend-led",
    dist_score:68.9, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.62, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8107, momentum_contribution:0.3482,
    value_carry_contribution:0.0012, tail_risk_penalty:0.15,
  },
  {
    symbol:"KEYS.L", company:"Keystone Law Group plc", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:475.0, currency:"GBp", ytd_pct:-28.11,
    iscore:70.4, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement defavorable - Cassure baissière majeure", dd_marche_status:"no",
    dd_moat_label:"Moat Narrow - cycle PH3a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 40.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 47)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 68.8%)", dd_valuation_status:"yes",
    fund_score:87.5, fund_label:"Satisfaisant",
    fund_value_creation:80.9, fund_solvency:96.2,
    roic_vs_wacc:40.5, mos:68.8, fcf_yield:0, peg:2.23,
    valuation_status:"",
    tscore:47.0, tscore_label:"Momentum en transition (tscore 47)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:18.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:3.36, dist_style:"Dividend-led",
    dist_score:74.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-3.77, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8761, momentum_contribution:0.3805,
    value_carry_contribution:0.0014, tail_risk_penalty:0.12,
  },
  {
    symbol:"ENPH", company:"Enphase Energy", sector:"NGM",
    cap_class:"Mid Cap", moat:"Asset Plays", region:"North America",
    country:"USA",
    price:0, currency:"34.920000", ytd_pct:-10.55,
    iscore:70.4, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Environnement de marche structurellement favorable. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur mitigee (ROIC-WACC -6.0 pts)", dd_value_status:"watch",
    dd_momentum_label:"Momentum en transition (tscore 51)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 22.4%)", dd_valuation_status:"yes",
    fund_score:91.5, fund_label:"Excellent",
    fund_value_creation:81.3, fund_solvency:89.5,
    roic_vs_wacc:-6.0, mos:22.4, fcf_yield:0, peg:-1.2,
    valuation_status:"",
    tscore:51.0, tscore_label:"Momentum en transition (tscore 51)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:21.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:4.35, dist_style:"Buyback-led",
    dist_score:66.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.12, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8963, momentum_contribution:0.3826,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"TLL.BO", company:"Trident Lifeline Limited", sector:"Health Care",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:263.5, currency:"INR", ytd_pct:-13.63,
    iscore:70.4, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: BUY.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle PH1a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 4.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 46)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 106.6%)", dd_valuation_status:"yes",
    fund_score:72.3, fund_label:"Solide",
    fund_value_creation:88.0, fund_solvency:93.7,
    roic_vs_wacc:4.0, mos:106.6, fcf_yield:0, peg:-1.37,
    valuation_status:"",
    tscore:46.0, tscore_label:"Momentum en transition (tscore 46)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.05, dist_style:"Buyback-led",
    dist_score:36.9, dist_sustainability:47.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.44, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.6592, momentum_contribution:0.3764,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"603605.SS", company:"Proya Cosmetics Co.", sector:"SSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"59.830000", ytd_pct:-14.28,
    iscore:70.3, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"warn", dd_global_score:62.3,
    dd_yes:2, dd_no:1, dd_watch:3,
    dd_summary:"Aucun facteur favorable dominant identifie. Momentum degrade (tscore 43) - attendre signal. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 24.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum defavorable - Signal faible · éviter ou alléger", dd_momentum_status:"no",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 70.0%)", dd_valuation_status:"yes",
    fund_score:93.7, fund_label:"Solide",
    fund_value_creation:90.2, fund_solvency:97.9,
    roic_vs_wacc:24.5, mos:70.0, fcf_yield:0, peg:0.71,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum defavorable - Signal faible · éviter ou alléger",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:20.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.65, dist_style:"Dividend-led",
    dist_score:73.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:0.98, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9266, momentum_contribution:0.2905,
    value_carry_contribution:0.0015, tail_risk_penalty:0.08,
  },
  {
    symbol:"600301.SS", company:"Nanning Chemical Industry Co.", sector:"SSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"46.600000", ytd_pct:-25.42,
    iscore:70.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 13.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 62)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 17.0%)", dd_valuation_status:"yes",
    fund_score:84.2, fund_label:"Excellent",
    fund_value_creation:87.1, fund_solvency:100.0,
    roic_vs_wacc:13.3, mos:17.0, fcf_yield:0, peg:0.2,
    valuation_status:"",
    tscore:62.0, tscore_label:"Momentum favorable (tscore 62)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:19.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:54.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-2.6, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8328, momentum_contribution:0.4202,
    value_carry_contribution:0.001, tail_risk_penalty:0.12,
  },
  {
    symbol:"002049.SZ", company:"Unigroup Guoxin Microelectronics Co.", sector:"SZSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"64.920000", ytd_pct:-17.14,
    iscore:70.2, iscore_tone:"pos",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Avantage concurrentiel et creation de valeur confirmes. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel identifie (Wide moat)", dd_moat_status:"yes",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 3.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 16.4%)", dd_valuation_status:"yes",
    fund_score:96.5, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:82.1,
    roic_vs_wacc:3.1, mos:16.4, fcf_yield:0, peg:-0.86,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:14.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:67.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.91, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8616, momentum_contribution:0.3957,
    value_carry_contribution:0.0011, tail_risk_penalty:0.15,
  },
  {
    symbol:"9534.SR", company:"Saudi Azm for Communication and Information Technology Company", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Narrow", region:"Middle East",
    country:"Saudi Arabia",
    price:21.9, currency:"SAR", ytd_pct:-7.82,
    iscore:70.2, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Avantage concurrentiel limite (Narrow moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 14.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 33.9%)", dd_valuation_status:"yes",
    fund_score:85.9, fund_label:"Solide",
    fund_value_creation:87.3, fund_solvency:95.2,
    roic_vs_wacc:14.2, mos:33.9, fcf_yield:0, peg:1.45,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.02, dist_style:"Buyback-led",
    dist_score:63.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.83, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8309, momentum_contribution:0.306,
    value_carry_contribution:0.0012, tail_risk_penalty:0.05,
  },
  {
    symbol:"2161.HK", company:"JBM (Healthcare) Limited", sector:"Health Care",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"Hong Kong",
    price:2.65, currency:"HKD", ytd_pct:0.0,
    iscore:70.2, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 13.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 43)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 57.0%)", dd_valuation_status:"yes",
    fund_score:79.8, fund_label:"Solide",
    fund_value_creation:0, fund_solvency:88.0,
    roic_vs_wacc:13.9, mos:57.0, fcf_yield:0, peg:0.03,
    valuation_status:"",
    tscore:43.0, tscore_label:"Momentum en transition (tscore 43)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:10.84, dist_style:"Dividend-led",
    dist_score:74.5, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.45, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8569, momentum_contribution:0.374,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"CSNA3.SA", company:"Companhia Siderúrgica Nacional", sector:"Materials",
    cap_class:"Small Cap", moat:"Narrow", region:"Latin America",
    country:"Saudi Arabia",
    price:6.47, currency:"BRL", ytd_pct:-29.19,
    iscore:70.1, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Environnement de marche structurellement favorable. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur mitigee (ROIC-WACC -3.6 pts)", dd_value_status:"watch",
    dd_momentum_label:"Momentum en transition (tscore 52)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 91.9%)", dd_valuation_status:"yes",
    fund_score:77.3, fund_label:"Satisfaisant",
    fund_value_creation:92.2, fund_solvency:66.1,
    roic_vs_wacc:-3.6, mos:91.9, fcf_yield:0, peg:0.01,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum en transition (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:46.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:24.44, dist_style:"Dividend-led",
    dist_score:48.2, dist_sustainability:22.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-8.21, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la qualité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.757, momentum_contribution:0.343,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"INFOBEAN.NS", company:"InfoBeans Technologies Limited", sector:"Information Technology",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:142.2, currency:"INR", ytd_pct:-84.94,
    iscore:70.1, iscore_tone:"pos",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Avantage concurrentiel limite (Narrow moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 0.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 50.2%)", dd_valuation_status:"yes",
    fund_score:80.2, fund_label:"Solide",
    fund_value_creation:89.9, fund_solvency:98.0,
    roic_vs_wacc:0.5, mos:50.2, fcf_yield:0, peg:-1.11,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:47.3, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.26, dist_style:"Dividend-led",
    dist_score:64.0, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.08, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7196, momentum_contribution:0.5113,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"CBE.PA", company:"Robertet SA", sector:"Materials",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"France",
    price:672.0, currency:"EUR", ytd_pct:-12.6,
    iscore:70.0, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2b a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 10.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 52)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 62.6%)", dd_valuation_status:"yes",
    fund_score:89.2, fund_label:"Solide",
    fund_value_creation:87.8, fund_solvency:97.5,
    roic_vs_wacc:10.1, mos:62.6, fcf_yield:0, peg:0.96,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum en transition (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:4.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:63.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:2.68, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et la qualité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8324, momentum_contribution:0.4058,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"6MK.DE", company:"Merck & Co.", sector:"FWB",
    cap_class:"Mega Cap", moat:"Asset Plays", region:"Europe",
    country:"Germany",
    price:0, currency:"103.600000", ytd_pct:0.38,
    iscore:69.9, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH3b a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 17.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 49)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 96.3%)", dd_valuation_status:"yes",
    fund_score:87.6, fund_label:"Excellent",
    fund_value_creation:86.1, fund_solvency:82.7,
    roic_vs_wacc:17.8, mos:96.3, fcf_yield:0, peg:0.0,
    valuation_status:"",
    tscore:49.0, tscore_label:"Momentum en transition (tscore 49)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:18.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:3.65, dist_style:"Dividend-led",
    dist_score:85.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.89, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8611, momentum_contribution:0.4172,
    value_carry_contribution:0.0014, tail_risk_penalty:0.05,
  },
  {
    symbol:"TKO", company:"TKO Group Holdings", sector:"NYSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"North America",
    country:"USA",
    price:0, currency:"203.760000", ytd_pct:-9.93,
    iscore:69.9, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH4a a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 1.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 53)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 78.9%)", dd_valuation_status:"yes",
    fund_score:90.1, fund_label:"Excellent",
    fund_value_creation:79.4, fund_solvency:66.8,
    roic_vs_wacc:1.0, mos:78.9, fcf_yield:0, peg:20.31,
    valuation_status:"",
    tscore:53.0, tscore_label:"Momentum favorable (tscore 53)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:22.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.7, dist_style:"Buyback-led",
    dist_score:52.2, dist_sustainability:47.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.63, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9004, momentum_contribution:0.4093,
    value_carry_contribution:0.0016, tail_risk_penalty:0.08,
  },
  {
    symbol:"300855.SZ", company:"Jiangsu ToLand Alloy Co.", sector:"SZSE",
    cap_class:"Mid Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"37.210000", ytd_pct:-23.68,
    iscore:69.9, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0J1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 9.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement degrade (Inconnu)", dd_rr_status:"no",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 415.6%)", dd_valuation_status:"yes",
    fund_score:100.0, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:82.4,
    roic_vs_wacc:9.4, mos:415.6, fcf_yield:0, peg:-1.69,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:1.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:67.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-3.42, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.732, momentum_contribution:0.3798,
    value_carry_contribution:0.0009, tail_risk_penalty:0.12,
  },
  {
    symbol:"CARGOTRANS.BO", company:"Cargotrans Maritime Limited", sector:"Industrials",
    cap_class:"Nano Cap", moat:"None", region:"Asia",
    country:"India",
    price:151.5, currency:"INR", ytd_pct:-5.96,
    iscore:69.9, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (None moat / signal BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 146.1%)", dd_valuation_status:"yes",
    fund_score:77.2, fund_label:"Solide",
    fund_value_creation:92.5, fund_solvency:97.2,
    roic_vs_wacc:12.1, mos:146.1, fcf_yield:0, peg:-0.64,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:20.51, dist_style:"Buyback-led",
    dist_score:59.2, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.96, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.7175, momentum_contribution:0.4209,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"OMAB.MX", company:"Grupo Aeroportuario del Centro Norte", sector:"BMV",
    cap_class:"Large Cap", moat:"Cyclicals", region:"Latin America",
    country:"Mexico",
    price:0, currency:"257.160000", ytd_pct:-2.3,
    iscore:69.9, iscore_tone:"warn",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (Margin moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 26.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 46.3%)", dd_valuation_status:"yes",
    fund_score:91.2, fund_label:"Excellent",
    fund_value_creation:92.0, fund_solvency:88.4,
    roic_vs_wacc:26.9, mos:46.3, fcf_yield:0, peg:-8.31,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:5.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:59.5, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.73, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9262, momentum_contribution:0.4234,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"MOUR.BR", company:"Moury Construct SA", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Europe",
    country:"Belgium",
    price:720.0, currency:"EUR", ytd_pct:24.11,
    iscore:69.8, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2b a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 15.0 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 48)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 48.5%)", dd_valuation_status:"yes",
    fund_score:90.3, fund_label:"Satisfaisant",
    fund_value_creation:84.0, fund_solvency:97.3,
    roic_vs_wacc:15.0, mos:48.5, fcf_yield:0, peg:33.58,
    valuation_status:"",
    tscore:48.0, tscore_label:"Momentum en transition (tscore 48)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:38.2, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:2.23, dist_style:"Dividend-led",
    dist_score:72.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.5, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8988, momentum_contribution:0.3941,
    value_carry_contribution:0.0013, tail_risk_penalty:0.05,
  },
  {
    symbol:"TKIM.JK", company:"PT Pabrik Kertas Tjiwi Kimia Tbk", sector:"Materials",
    cap_class:"Nano Cap", moat:"None", region:"Southeast Asia",
    country:"Indonesia",
    price:8400.0, currency:"IDR", ytd_pct:22.34,
    iscore:69.8, iscore_tone:"warn",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:70.3,
    dd_yes:3, dd_no:1, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Avantage concurrentiel limite (None moat / signal STRONG_BUY)", dd_moat_status:"no",
    dd_value_label:"Creation de valeur mitigee (ROIC-WACC -4.7 pts)", dd_value_status:"watch",
    dd_momentum_label:"Momentum favorable (tscore 55)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 75.9%)", dd_valuation_status:"yes",
    fund_score:83.0, fund_label:"Satisfaisant",
    fund_value_creation:78.8, fund_solvency:89.6,
    roic_vs_wacc:-4.7, mos:75.9, fcf_yield:0, peg:0.05,
    valuation_status:"",
    tscore:55.0, tscore_label:"Momentum favorable (tscore 55)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:-7.3, bt_5y_underperf:true, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:41.3, dist_sustainability:57.5,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.6, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8559, momentum_contribution:0.4593,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"6378.T", company:"Kimura Chemical Plants Co.", sector:"TSE",
    cap_class:"Large Cap", moat:"Stalwarts", region:"Asia",
    country:"Japan",
    price:0, currency:"1371.000000", ytd_pct:2.43,
    iscore:69.8, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 8.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 58)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 57.6%)", dd_valuation_status:"yes",
    fund_score:79.7, fund_label:"Solide",
    fund_value_creation:77.7, fund_solvency:97.0,
    roic_vs_wacc:8.1, mos:57.6, fcf_yield:0, peg:0.11,
    valuation_status:"",
    tscore:58.0, tscore_label:"Momentum favorable (tscore 58)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:14.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:65.2, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-4.02, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.7732, momentum_contribution:0.3881,
    value_carry_contribution:0.0013, tail_risk_penalty:0.12,
  },
  {
    symbol:"NAVF.L", company:"Nippon Active Value Fund plc", sector:"Financials",
    cap_class:"Small Cap", moat:"Wide", region:"Asia",
    country:"UK",
    price:223.0, currency:"GBp", ytd_pct:7.25,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Profil risque/rendement attractif sur la periode. Signal: BUY.",
    dd_marche_label:"Marche en transition - MODERATE", dd_marche_status:"watch",
    dd_moat_label:"Moat Wide - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 6.2 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 81.5%)", dd_valuation_status:"yes",
    fund_score:91.0, fund_label:"Excellent",
    fund_value_creation:76.8, fund_solvency:100.0,
    roic_vs_wacc:6.2, mos:81.5, fcf_yield:0, peg:-0.19,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:20.6, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:55.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.87, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.9806, momentum_contribution:0.5032,
    value_carry_contribution:0.0015, tail_risk_penalty:0.05,
  },
  {
    symbol:"OVOBELE.BO", company:"Ovobel Foods Limited", sector:"Consumer Staples",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"India",
    price:114.0, currency:"INR", ytd_pct:-47.64,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0D1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 15.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 60)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 100.0%)", dd_valuation_status:"yes",
    fund_score:81.1, fund_label:"Fragile",
    fund_value_creation:90.0, fund_solvency:95.3,
    roic_vs_wacc:15.4, mos:100.0, fcf_yield:0, peg:-0.21,
    valuation_status:"",
    tscore:60.0, tscore_label:"Momentum favorable (tscore 60)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:40.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Buyback-led",
    dist_score:53.3, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.69, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7503, momentum_contribution:0.5797,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"EMAE4.SA", company:"Empresa Metropolitana de Águas e Energia S.A.", sector:"Utilities",
    cap_class:"Nano Cap", moat:"Narrow", region:"Latin America",
    country:"Saudi Arabia",
    price:40.39, currency:"BRL", ytd_pct:-8.26,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"ACCUMULATE", dd_global_tone:"warn", dd_global_score:62.3,
    dd_yes:2, dd_no:1, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. EVA negative (ROIC-WACC -2.8 pts). Signal: ACCUMULATE.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH4 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Destruction de valeur (ROIC-WACC -2.8 pts)", dd_value_status:"no",
    dd_momentum_label:"Momentum en transition (tscore 50)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 71.8%)", dd_valuation_status:"yes",
    fund_score:77.7, fund_label:"Solide",
    fund_value_creation:28.6, fund_solvency:97.0,
    roic_vs_wacc:-2.8, mos:71.8, fcf_yield:0, peg:-0.45,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum en transition (tscore 50)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:36.8, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:64.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.15, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8592, momentum_contribution:0.3842,
    value_carry_contribution:0.0014, tail_risk_penalty:0.15,
  },
  {
    symbol:"5271.KL", company:"Pecca Group Berhad", sector:"Consumer Discretionary",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Malaysia",
    price:1.54, currency:"MYR", ytd_pct:-11.31,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0C2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 27.8 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 52)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 52.8%)", dd_valuation_status:"yes",
    fund_score:92.9, fund_label:"Excellent",
    fund_value_creation:90.8, fund_solvency:99.0,
    roic_vs_wacc:27.8, mos:52.8, fcf_yield:0, peg:0.3,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum en transition (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:61.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:9.67, dist_style:"Dividend-led",
    dist_score:77.4, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.43, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9182, momentum_contribution:0.3544,
    value_carry_contribution:0.0013, tail_risk_penalty:0.08,
  },
  {
    symbol:"HATM.JK", company:"PT Habco Trans Maritima Tbk", sector:"Industrials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Southeast Asia",
    country:"Indonesia",
    price:304.0, currency:"IDR", ytd_pct:-24.64,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:78.3,
    dd_yes:4, dd_no:1, dd_watch:1,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0A1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 2.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 55)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement degrade (Inconnu)", dd_rr_status:"no",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 24.8%)", dd_valuation_status:"yes",
    fund_score:85.8, fund_label:"Excellent",
    fund_value_creation:92.3, fund_solvency:98.9,
    roic_vs_wacc:2.5, mos:24.8, fcf_yield:0, peg:-0.49,
    valuation_status:"",
    tscore:55.0, tscore_label:"Momentum favorable (tscore 55)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:51.4, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.16, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8159, momentum_contribution:0.3947,
    value_carry_contribution:0.0015, tail_risk_penalty:0.05,
  },
  {
    symbol:"HSY.BA", company:"The Hershey Company", sector:"Consumer Staples",
    cap_class:"Nano Cap", moat:"Narrow", region:"North America",
    country:"Argentina",
    price:14300.0, currency:"ARS", ytd_pct:10.7,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Marche favorable et profil risque/rendement attractif. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 27.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement attractif (Inconnu)", dd_rr_status:"yes",
    dd_valuation_label:"Valorisation proche valeur juste (MOS -10000.0%)", dd_valuation_status:"watch",
    fund_score:89.7, fund_label:"Excellent",
    fund_value_creation:90.1, fund_solvency:73.5,
    roic_vs_wacc:27.4, mos:-300, fcf_yield:0, peg:0.25,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:12.06, dist_style:"Dividend-led",
    dist_score:77.8, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:5.04, er_reading:"Correct", er_synthesis:"Lecture synthétique : profil correct. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8892, momentum_contribution:0.5174,
    value_carry_contribution:0.0011, tail_risk_penalty:0.05,
  },
  {
    symbol:"PAC", company:"Grupo Aeroportuario del Pacífico", sector:"NYSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Latin America",
    country:"Mexico",
    price:0, currency:"252.270000", ytd_pct:-5.33,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 19.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 89.5%)", dd_valuation_status:"yes",
    fund_score:90.8, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:71.4,
    roic_vs_wacc:19.4, mos:89.5, fcf_yield:0, peg:-2.98,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:26.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:7.59, dist_style:"Balanced",
    dist_score:63.7, dist_sustainability:70.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.44, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8979, momentum_contribution:0.3793,
    value_carry_contribution:0.0011, tail_risk_penalty:0.05,
  },
  {
    symbol:"TGA.L", company:"Thungela Resources Limited", sector:"Energy",
    cap_class:"Large Cap", moat:"Narrow", region:"Africa",
    country:"UK",
    price:753.0, currency:"GBp", ytd_pct:75.53,
    iscore:69.7, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 3.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 44)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 90.5%)", dd_valuation_status:"yes",
    fund_score:92.2, fund_label:"Excellent",
    fund_value_creation:92.5, fund_solvency:97.2,
    roic_vs_wacc:3.7, mos:90.5, fcf_yield:0, peg:-0.18,
    valuation_status:"",
    tscore:44.0, tscore_label:"Momentum en transition (tscore 44)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:12.98, dist_style:"Dividend-led",
    dist_score:79.6, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-6.85, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8758, momentum_contribution:0.253,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"603338.SS", company:"Zhejiang Dingli Machinery Co.", sector:"SSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"47.840000", ytd_pct:-18.39,
    iscore:69.6, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 10.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 43)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 63.5%)", dd_valuation_status:"yes",
    fund_score:90.9, fund_label:"Excellent",
    fund_value_creation:0, fund_solvency:87.8,
    roic_vs_wacc:10.9, mos:63.5, fcf_yield:0, peg:-1.57,
    valuation_status:"",
    tscore:43.0, tscore_label:"Momentum en transition (tscore 43)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:10.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:69.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.82, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8759, momentum_contribution:0.3522,
    value_carry_contribution:0.0014, tail_risk_penalty:0.15,
  },
  {
    symbol:"300820.SZ", company:"Sichuan Injet Electric Stock Co.", sector:"SZSE",
    cap_class:"Large Cap", moat:"Asset Plays", region:"Asia",
    country:"China",
    price:0, currency:"43.700000", ytd_pct:-21.28,
    iscore:69.6, iscore_tone:"warn",
    dd_signal:"ACCUMULATE", dd_global_tone:"pos", dd_global_score:68.0,
    dd_yes:2, dd_no:0, dd_watch:4,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - CONCENTRATED", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 10.7 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 48)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 103.9%)", dd_valuation_status:"yes",
    fund_score:87.9, fund_label:"Solide",
    fund_value_creation:92.5, fund_solvency:97.6,
    roic_vs_wacc:10.7, mos:103.9, fcf_yield:0, peg:-1.42,
    valuation_status:"",
    tscore:48.0, tscore_label:"Momentum en transition (tscore 48)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:30.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:1.14, dist_style:"Dividend-led",
    dist_score:54.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.39, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8117, momentum_contribution:0.4138,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"GGB", company:"Gerdau S.A.", sector:"Materials",
    cap_class:"Large Cap", moat:"Narrow", region:"Latin America",
    country:"Brazil",
    price:3.78, currency:"USD", ytd_pct:-2.17,
    iscore:69.5, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0B1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 1.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 53)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 58.7%)", dd_valuation_status:"yes",
    fund_score:91.3, fund_label:"Solide",
    fund_value_creation:91.1, fund_solvency:97.6,
    roic_vs_wacc:1.6, mos:58.7, fcf_yield:0, peg:-0.21,
    valuation_status:"",
    tscore:53.0, tscore_label:"Momentum favorable (tscore 53)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:28.1, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:7.62, dist_style:"Balanced",
    dist_score:83.8, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.2, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8832, momentum_contribution:0.4389,
    value_carry_contribution:0.0015, tail_risk_penalty:0.15,
  },
  {
    symbol:"002088.SZ", company:"Luyang Energy-Saving Materials Co.", sector:"SZSE",
    cap_class:"Mid Cap", moat:"Cyclicals", region:"Asia",
    country:"China",
    price:0, currency:"9.700000", ytd_pct:-16.54,
    iscore:69.5, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0H1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.4 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 48)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 57.4%)", dd_valuation_status:"yes",
    fund_score:90.3, fund_label:"Solide",
    fund_value_creation:87.3, fund_solvency:98.3,
    roic_vs_wacc:12.4, mos:57.4, fcf_yield:0, peg:-4.4,
    valuation_status:"",
    tscore:48.0, tscore_label:"Momentum en transition (tscore 48)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:38.9, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:7.0, dist_style:"Dividend-led",
    dist_score:74.9, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.81, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont l'illiquidité et la croissance. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"l'illiquidité", top_headwind:"",
    quality_contribution:0.8733, momentum_contribution:0.3154,
    value_carry_contribution:0.0013, tail_risk_penalty:0.15,
  },
  {
    symbol:"FEX.AX", company:"Fenix Resources Limited", sector:"Materials",
    cap_class:"Nano Cap", moat:"Narrow", region:"Asia",
    country:"Australia",
    price:0.32, currency:"AUD", ytd_pct:-36.08,
    iscore:69.5, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: BUY.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0B a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 15.3 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 53)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 91.4%)", dd_valuation_status:"yes",
    fund_score:94.7, fund_label:"Excellent",
    fund_value_creation:88.2, fund_solvency:98.4,
    roic_vs_wacc:15.3, mos:91.4, fcf_yield:0, peg:-1.62,
    valuation_status:"",
    tscore:53.0, tscore_label:"Momentum favorable (tscore 53)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:80.5, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:85.1, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:4.95, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9432, momentum_contribution:0.4019,
    value_carry_contribution:0.0016, tail_risk_penalty:0.05,
  },
  {
    symbol:"SQZ.L", company:"Serica Energy plc", sector:"Energy",
    cap_class:"Small Cap", moat:"Narrow", region:"Europe",
    country:"UK",
    price:277.5, currency:"GBp", ytd_pct:68.19,
    iscore:69.4, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Aucun facteur favorable dominant identifie. Signal: BUY.",
    dd_marche_label:"Marche en transition - MODERATE", dd_marche_status:"watch",
    dd_moat_label:"Moat Narrow - cycle 0H1 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 9.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 52)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 30.7%)", dd_valuation_status:"yes",
    fund_score:86.2, fund_label:"Solide",
    fund_value_creation:0, fund_solvency:87.8,
    roic_vs_wacc:9.5, mos:30.7, fcf_yield:0, peg:-0.36,
    valuation_status:"",
    tscore:52.0, tscore_label:"Momentum favorable (tscore 52)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:33.7, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:23.96, dist_style:"Dividend-led",
    dist_score:78.7, dist_sustainability:90.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:1.58, er_reading:"Peu attractif", er_synthesis:"Lecture synthétique : profil peu attractif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9122, momentum_contribution:0.3669,
    value_carry_contribution:0.0016, tail_risk_penalty:0.08,
  },
  {
    symbol:"HMY", company:"Harmony Gold Mining Company Limited", sector:"Materials",
    cap_class:"Large Cap", moat:"Narrow", region:"Africa",
    country:"South Africa",
    price:16.1, currency:"USD", ytd_pct:-22.76,
    iscore:69.4, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:84.0,
    dd_yes:4, dd_no:0, dd_watch:2,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH2 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 15.6 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 56)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 90.9%)", dd_valuation_status:"yes",
    fund_score:83.4, fund_label:"Fragile",
    fund_value_creation:88.4, fund_solvency:97.5,
    roic_vs_wacc:15.6, mos:90.9, fcf_yield:0, peg:0.16,
    valuation_status:"",
    tscore:56.0, tscore_label:"Momentum favorable (tscore 56)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:22.4, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:78.3, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.75, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.7927, momentum_contribution:0.3443,
    value_carry_contribution:0.0009, tail_risk_penalty:0.15,
  },
  {
    symbol:"DIG.WA", company:"Digital Network SA", sector:"Communication Services",
    cap_class:"Nano Cap", moat:"Narrow", region:"Europe",
    country:"Poland",
    price:169.6, currency:"PLN", ytd_pct:-0.61,
    iscore:69.4, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Valorisation sans marge de securite exploitable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle PH4 a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 40.1 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum favorable (tscore 63)", dd_momentum_status:"yes",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Valorisation proche valeur juste (MOS 58.6%)", dd_valuation_status:"watch",
    fund_score:77.0, fund_label:"Solide",
    fund_value_creation:0, fund_solvency:88.0,
    roic_vs_wacc:40.1, mos:58.6, fcf_yield:0, peg:0.5,
    valuation_status:"",
    tscore:63.0, tscore_label:"Momentum favorable (tscore 63)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"green",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:79.0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0, dist_style:"Dividend-led",
    dist_score:70.6, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:-5.26, er_reading:"Négatif", er_synthesis:"Lecture synthétique : profil négatif. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est très lourde.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9188, momentum_contribution:0.4319,
    value_carry_contribution:0.0014, tail_risk_penalty:0.15,
  },
  {
    symbol:"INPST.AS", company:"InPost S.A.", sector:"Industrials",
    cap_class:"Large Cap", moat:"Narrow", region:"Europe",
    country:"Netherlands",
    price:15.13, currency:"EUR", ytd_pct:44.03,
    iscore:69.4, iscore_tone:"warn",
    dd_signal:"BUY", dd_global_tone:"pos", dd_global_score:76.0,
    dd_yes:3, dd_no:0, dd_watch:3,
    dd_summary:"Environnement de marche structurellement favorable. Signal: BUY.",
    dd_marche_label:"Environnement de marche favorable", dd_marche_status:"yes",
    dd_moat_label:"Moat Narrow - cycle 0Z a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 12.9 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum en transition (tscore 39)", dd_momentum_status:"watch",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 42.9%)", dd_valuation_status:"yes",
    fund_score:85.2, fund_label:"Solide",
    fund_value_creation:92.5, fund_solvency:76.4,
    roic_vs_wacc:12.9, mos:42.9, fcf_yield:0, peg:0.31,
    valuation_status:"",
    tscore:39.0, tscore_label:"Momentum en transition (tscore 39)",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"red",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:0.56, dist_style:"Buyback-led",
    dist_score:53.1, dist_sustainability:80.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.71, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.8358, momentum_contribution:0.2621,
    value_carry_contribution:0.0009, tail_risk_penalty:0.05,
  },
  {
    symbol:"VISA.NE", company:"Visa Inc.", sector:"Financials",
    cap_class:"Large Cap", moat:"Wide", region:"North America",
    country:"Canada",
    price:27.72, currency:"CAD", ytd_pct:-14.42,
    iscore:69.3, iscore_tone:"warn",
    dd_signal:"ACCUMULATE", dd_global_tone:"warn", dd_global_score:62.3,
    dd_yes:2, dd_no:1, dd_watch:3,
    dd_summary:"Aucun facteur favorable dominant identifie. Momentum degrade (tscore 38) - attendre signal. Signal: ACCUMULATE.",
    dd_marche_label:"Marche en transition - LOW", dd_marche_status:"watch",
    dd_moat_label:"Moat Wide - cycle 0F a surveiller", dd_moat_status:"watch",
    dd_value_label:"Creation de valeur averee (ROIC-WACC 28.5 pts)", dd_value_status:"yes",
    dd_momentum_label:"Momentum defavorable - Signal faible · éviter ou alléger", dd_momentum_status:"no",
    dd_rr_label:"Profil risque/rendement acceptable (Inconnu)", dd_rr_status:"watch",
    dd_valuation_label:"Decote sur valeur intrinseque (MOS 85.8%)", dd_valuation_status:"yes",
    fund_score:92.8, fund_label:"Solide",
    fund_value_creation:81.5, fund_solvency:93.8,
    roic_vs_wacc:28.5, mos:85.8, fcf_yield:0, peg:0.08,
    valuation_status:"",
    tscore:50.0, tscore_label:"Momentum defavorable - Signal faible · éviter ou alléger",
    tsmom_3m:0.0, tsmom_6m:0.0, tsmom_12m:0.0, rsi14:50.0,
    timing_structure_tone:"amber",
    rr_quadrant:"Inconnu", rr_profile_short:"",
    rr_dd_max:0.0, rr_sharpe:0.0, rr_irrs_score:0.0,
    bt_5y_cagr_tr:0, bt_5y_underperf:false, bt_5y_dd:0.0,
    dist_shareholder_yield:64.03, dist_style:"Buyback-led",
    dist_score:81.7, dist_sustainability:100.0,
    ef_cfo_coverage:0, ef_actionnaire_covered:false,
    structure_phase:"",
    er_proxy:3.48, er_reading:"Faible", er_synthesis:"Lecture synthétique : profil modeste. Les principaux moteurs sont la croissance et l'illiquidité. Aucun frein négatif majeur. La pénalité de risque est élevée.",
    top_driver:"la croissance", top_headwind:"",
    quality_contribution:0.9484, momentum_contribution:0.2781,
    value_carry_contribution:0.0016, tail_risk_penalty:0.05,
  }
];

function buildPiliers(data) {
  const SECTORS   = ["Tous",...new Set(data.map(s=>s.sector).filter(Boolean))].sort((a,b)=>a==="Tous"?-1:a.localeCompare(b));
  const MOATS     = ["Tous","Wide","Narrow","None"];
  const SIGNALS   = ["Tous","BUY","ACCUMULATE","HOLD","SELL","REDUCE"];
  const CAP_CL    = ["Tous","Mega Cap","Large Cap","Mid Cap","Small Cap","Nano Cap"];
  const REGIONS = buildRegionFilterOptions(data);
  const COUNTRIES = [
    "Tous",
    ...new Set(
      data
        .map((s) => String(s.country || "").trim())
        .filter((c) => c && isCountryCode(c))
    ),
  ].sort((a, b) => (a === "Tous" ? -1 : a.localeCompare(b, "fr")));
  const QUADRANTS = ["Tous","Efficace","High Return / Low Risk","Équilibré","Déséquilibré","Dangereux","Inconnu"];
  const PHASES    = ["Tous",...new Set(data.map(s=>s.structure_phase).filter(Boolean))].sort((a,b)=>a==="Tous"?-1:a.localeCompare(b));
  const DIST_ST   = ["Tous",...new Set(data.map(s=>s.dist_style).filter(Boolean))].sort((a,b)=>a==="Tous"?-1:a.localeCompare(b));

  return [
  {
    id:"profil", label:"Profil", icon:"◈",
    filters:[
      { id:"signal",   label:"Signal décision",  type:"pills", options:SIGNALS, field:"dd_signal",
        tt:{ d:"Signal synthétique issu des 6 facteurs décisionnels Invelps.", u:"BUY/ACCUMULATE pour convergence positive. HOLD à surveiller.", i:"BUY ≥ 4 facteurs favorables. HOLD = équilibre. SELL ≥ 3 défavorables." }},
      { id:"moat",     label:"Avantage Compétitif", type:"pills", options:MOATS,   field:"moat",
        tt:{ d:"Avantage concurrentiel durable (Morningstar/Damodaran).", u:"Wide = quality compétitive durable. Narrow = value avec upside. None = risque structurel.", i:"Wide = pricing power ou switching costs. None = sans protection structurelle." }},
      { id:"capClass", label:"Capitalisation",   type:"pills", options:CAP_CL,  field:"cap_class",
        tt:{ d:"Taille par capitalisation boursière.", u:"Large Cap pour stabilité. Mid/Small Cap pour croissance.", i:"Large > 10Mds€. Mid 2–10Mds€. Small < 2Mds€." }},
      { id:"region",   label:"Région",            type:"pills", options:REGIONS, field:"region",
        tt:{ d:"Zone géographique principale (alignée Market Leaders : Asia, Europe, Southeast Asia, North America, Latin America...).", u:"Diversification ou ciblage sectoriel.", i:"Attention aux biais de change et risques géopolitiques." }},
      { id:"country",  label:"Pays",               type:"pills", options:COUNTRIES, field:"country",
        tt:{ d:"Pays de cotation / domicile (27 pays disponibles : Indonésie 13, Chine 13, Inde 10, UK 10, USA 8, Japon 6...).", u:"Filtrer par pays pour concentrer l'exposition ou éviter un marché spécifique.", i:"Cohérent avec la vue Country Performance de Market Leaders. Utile pour gérer le risque pays." }},
      { id:"sector",   label:"Secteur GICS",      type:"pills", options:SECTORS, field:"sector",
        tt:{ d:"Secteur GICS de l'entreprise.", u:"Combinez avec Moat et phase marché.", i:"Tech/Santé tolèrent des PE élevés si croissance soutenue." }},
    ],
  },
  {
    id:"decisions", label:"Facteurs Décision", icon:"⊕",
    filters:[
      { id:"minIscore", label:"IScore global min",       type:"range", min:0, max:100, field:"iscore", unit:"",
        tt:{ d:"Score composite : Fondamentaux 25%, Valorisation 20%, Marché 15%, Momentum 20%, R/R 20%.", u:"≥ 65 pour qualité. ≥ 50 pour univers élargi.", i:"< 50 = profil dégradé. 65–80 = solide. > 80 = conviction forte." }},
      { id:"minYes",    label:"Facteurs favorables min", type:"range", min:0, max:6,   field:"dd_yes", unit:"/6",
        tt:{ d:"Nombre de facteurs décisionnels signalant positivement parmi les 6.", u:"≥ 4 = forte conviction. Parallèle à l'IScore.", i:"IScore élevé avec peu de convergences = construction fragile." }},
    ],
  },
  {
    id:"fondamentaux", label:"Fondamentaux", icon:"▣",
    filters:[
      { id:"minFund", label:"Score fondamental min",          type:"range", min:0, max:100, field:"fund_score",           unit:"",
        tt:{ d:"Agrégation 9 piliers : liquidité, profitabilité, croissance, efficacité, rentabilité, cash-flow, solvabilité, allocation, création valeur.", u:"≥ 65 = fondamentaux solides. < 55 = risque bilanciel.", i:"Score élevé sans EVA positive = croissance non rentable." }},
      { id:"minEVA",  label:"EVA min (ROIC-WACC pts)",  type:"range", min:-20, max:60, field:"roic_vs_wacc",  unit:" pts", step:1,
        tt:{ d:"ROIC − WACC en points : mesure directe de la création de valeur économique. C'est la valeur affichée dans la colonne EVA du tableau.", u:"≥ 5 pts = création de valeur avérée. ≥ 15 pts = fort différentiel. < 0 = destruction de valeur.", i:"C'est le filtre affiché dans la colonne EVA. Ne pas confondre avec le score fondamental de création de valeur (0–100). (Damodaran/McKinsey)" }},
      { id:"minSolv", label:"Solvabilité min",                type:"range", min:0, max:100, field:"fund_solvency",        unit:"",
        tt:{ d:"Levier financier, couverture intérêts, autonomie bilancielle.", u:"≥ 60 pour limiter le risque en cycle baissier.", i:"Score faible dans un contexte de taux élevés = risque de refinancement." }},
    ],
  },
  {
    id:"valorisation", label:"Valorisation", icon:"◎",
    filters:[
      { id:"minMos",  label:"Marge de sécurité min", type:"range", min:-80, max:50, field:"mos",       unit:"%",
        tt:{ d:"MOS > 0 = prix EN DESSOUS de la valeur intrinsèque (décote). MOS < 0 = prix AU-DESSUS (surévaluation).", u:"Graham recommandait ≥ 25% pour value. Growth tolère légèrement négatif si qualité élevée.", i:"MOS élevée + fondamentaux faibles = value trap. MOS faible + fondamentaux solides = croissance chère." }},
      { id:"minFcfY", label:"FCF Yield min",          type:"range", min:0,   max:20, field:"fcf_yield", unit:"%",
        tt:{ d:"Free Cash Flow / Capitalisation. Rendement réel de trésorerie pour actionnaires.", u:"≥ 4% = attractif pour une stratégie value.", i:"FCF Yield élevé dans un secteur déclinant ≠ opportunité." }},
      { id:"maxPeg",  label:"PEG ratio max",           type:"range", min:0,   max:5,  field:"peg",       unit:"x", isMax:true, step:0.1,
        tt:{ d:"PE / taux de croissance bénéfices. Mesure Lynch/Graham.", u:"< 1 = potentiellement sous-valorisé compte tenu de la croissance.", i:"Perd de pertinence pour secteurs cycliques et sociétés déficitaires." }},
    ],
  },
  {
    id:"marche", label:"Structure de Marché", icon:"◉",
    filters:[
      { id:"structTone", label:"Tonalité structure", type:"pills",
        options:["Tous","green","amber","red"], field:"timing_structure_tone",
        tt:{ d:"Tonalité structurelle issue du TScore et de l'analyse de la structure de marché.", u:"green = structure favorable, momentum confirmé. amber = transition. red = structure dégradée.", i:"Combiné au TScore ≥ 60, green = meilleure fenêtre d'entrée (Ilmanen/AQR timing signals)." }},
      { id:"marcheStatus", label:"Signal marché", type:"pills",
        options:["Tous","yes","watch","no"], field:"dd_marche_status",
        tt:{ d:"Évaluation de l'environnement macro et sectoriel (Decisions Engine).", u:"yes = environnement structurellement favorable. watch = neutre/transition. no = défavorable.", i:"Un marché favorable + moat narrow peut compenser une valorisation tendue (Porter, McKinsey)." }},
      { id:"moatStatus",  label:"Signal moat",   type:"pills",
        options:["Tous","yes","watch","no"], field:"dd_moat_status",
        tt:{ d:"Évaluation de la solidité du avantage compétitif (Decisions Engine).", u:"yes = moat confirmé. watch = moat étroit ou à surveiller. no = avantage compétitif fragile.", i:"Moat wide + EVA positive = profil quality compounder (Damodaran, Morningstar)." }},
    ],
  },
  {
    id:"timing", label:"Timing", icon:"◷",
    filters:[
      { id:"minTs",    label:"TScore global min",  type:"range", min:0,   max:100, field:"tscore",   unit:"",
        tt:{ d:"Score composite technique : tendance, momentum, structure de prix, volumes, signaux SMA.", u:"≥ 60 = momentum favorable. < 40 = pression vendeuse.", i:"TScore ≠ valorisation. C'est du timing. Croiser avec MOS pour les fenêtres d'entrée." }},
      { id:"minMom3m", label:"Momentum 3M min",    type:"range", min:-40, max:40,  field:"tsmom_3m", unit:"%",
        tt:{ d:"Performance relative 3 mois (TS-Momentum, Ilmanen/AQR).", u:"Éviter < -15% sauf catalyseur identifié.", i:"Persistance statistique jusqu'à 12 mois (Moskowitz, Ooi, Pedersen)." }},
      { id:"minRsi",   label:"RSI 14 min",          type:"range", min:0,   max:100, field:"rsi14",    unit:"",
        tt:{ d:"Relative Strength Index sur 14 périodes.", u:"< 30 = survente. > 70 = surachat. 40–60 = neutre.", i:"En tendance baissière forte, le RSI peut rester en survente longtemps." }},
    ],
  },
  {
    id:"rr", label:"Risque / Rendement", icon:"◈",
    filters:[
      { id:"rrQ",       label:"Quadrant R/R",     type:"pills", options:QUADRANTS, field:"rr_quadrant",
        tt:{ d:"Classification CAGR pondéré vs drawdown max historique.", u:"Efficace/High Return Low Risk = profils optimaux. Déséquilibré/Dangereux = à éviter.", i:"Compléter avec le Sharpe ratio. Dépendant de la période analysée." }},
      { id:"minCagr5y", label:"CAGR TR 5Y min",   type:"range", min:-20, max:70, field:"bt_5y_cagr_tr", unit:"%",
        tt:{ d:"CAGR Total Return (prix + dividendes réinvestis) sur 5 ans en backtest.", u:"Seuil ≥ 8–10% (taux sans risque + prime risque).", i:"Comparer systématiquement au benchmark." }},
      { id:"minDD",     label:"Drawdown max ≥",   type:"range", min:-80, max:0,  field:"rr_dd_max",     unit:"%",
        tt:{ d:"Perte maximale depuis un plus haut sur 5 ans.", u:"≤ -30% pour profils prudents.", i:"Drawdown élevé + récupération longue = profil fragile." }},
      { id:"minSharpe", label:"Sharpe ratio min",  type:"range", min:-1,  max:3,  field:"rr_sharpe",     unit:"", step:0.1,
        tt:{ d:"Excès de rendement par unité de volatilité (Sharpe, 1966).", u:"> 1 = excellent. 0.5–1 = acceptable. < 0 = sous le taux sans risque.", i:"Sensible à la période. Compléter avec le Sortino." }},
      { id:"minErProxy", label:"Rendement attendu AQR min", type:"range", min:-10, max:15, field:"er_proxy", unit:"%", step:0.5,
        tt:{ d:"Expected Return proxy Ilmanen/AQR : quality × carry × momentum × growth − tail risk. Exprimé en % annualisé.", u:"≥ 3% = contribution factorielle favorable. < 0% = vents contraires (momentum, carry, quality négatifs).", i:"Forward-looking. Un ER négatif ne signifie pas une baisse certaine. Driver principal = top_driver." }},
    ],
  },
  {
    id:"ef", label:"État Financier", icon:"≡",
    filters:[
      { id:"minCfo",  label:"Couverture CFO min",               type:"range", min:0, max:110, field:"ef_cfo_coverage", unit:"%",
        tt:{ d:"% des besoins couverts par le CFO.", u:"< 25% = dépendance externe. ≥ 50% = confort. > 100% = générateur net de cash.", i:"CFO insuffisant dans un contexte de taux élevés = risque de refinancement." }},
      { id:"actCov",  label:"Rémunération actionnaire couverte", type:"bool",  field:"ef_actionnaire_covered",
        tt:{ d:"Dividendes + rachats financés par le CFO réel.", u:"Activez pour exclure les sociétés qui s'endettent pour rémunérer.", i:"Non couvert = politique non soutenable à moyen terme (Vernimmen)." }},
    ],
  },
  {
    id:"backtest", label:"Backtest", icon:"◫",
    filters:[
      { id:"outperf", label:"Surperformance 5Y vs benchmark", type:"bool", field:"bt_5y_underperf", invert:true,
        tt:{ d:"Surperformance vs indice de référence sur 5 ans en total return.", u:"Activez pour ne retenir que les générateurs d'alpha prouvés.", i:"Un titre value peut sous-performer 3–5 ans avant re-rating." }},
    ],
  },
  {
    id:"distribution", label:"Distribution", icon:"⊞",
    filters:[
      { id:"minShY",    label:"Shareholder Yield min",   type:"range", min:0,  max:20,  field:"dist_shareholder_yield", unit:"%",
        tt:{ d:"Dividende yield + buyback yield. Retour actionnaire total.", u:"≥ 4% pour profil income. ≥ 8% avec solvabilité solide = attractif.", i:"Yield élevé financé par la dette = destruction de valeur masquée (Brealey/Myers)." }},
      { id:"distSt",    label:"Style de distribution",   type:"pills", options:DIST_ST, field:"dist_style",
        tt:{ d:"Mode privilégié : dividendes vs rachats.", u:"Buyback-led favorise la croissance BPA. Dividend-led pour profils income.", i:"Rachats créent de la valeur uniquement si l'action est sous-valorisée (Jensen, 1986)." }},
    ],
  },
  ];
}

// ── TOOLTIP : MODAL CENTRÉ SUR MOBILE, POSITIONNEMENT INTELLIGENT SUR DESKTOP
function Tooltip({ content, isDark, isMobile, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top:0, left:0, dir:"right" });
  const ref = useRef(null);
  const TIP_W = 240;

  const compute = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prefer bottom, then top
    const spaceRight = vw - r.right;
    const spaceLeft  = r.left;
    let dir = spaceRight >= TIP_W + 12 ? "right" : spaceLeft >= TIP_W + 12 ? "left" : "bottom";
    setPos({ top: r.top + window.scrollY, left: r.left, rect: r, dir, vw, vh });
  }, []);

  const show = useCallback((e) => {
    e.stopPropagation();
    compute();
    setOpen(true);
  }, [compute]);

  const hide = useCallback(() => setOpen(false), []);

  // On mobile: tap to toggle, click outside to close
  useEffect(() => {
    if (!open || !isMobile) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("touchstart", fn, { passive:true });
    return () => document.removeEventListener("touchstart", fn);
  }, [open, isMobile]);

  const tipStyle = useMemo(() => {
    if (isMobile) return {
      position:"fixed",
      bottom: 16, left: 12, right: 12,
      zIndex: 9999,
      background:"var(--tip-bg)",
      border:`1px solid ${isDark?"rgba(148,163,184,0.25)":"rgba(30,40,34,0.15)"}`,
      borderRadius:12, padding:"14px 16px",
      boxShadow: "0 -4px 32px rgba(0,0,0,0.25)",
    };
    if (!pos.rect) return { display:"none" };
    const { rect, dir, vw, vh } = pos;
    const base = {
      position:"fixed", zIndex:9999, width:TIP_W,
      background:"var(--tip-bg)",
      border:`1px solid ${isDark?"rgba(148,163,184,0.22)":"rgba(30,40,34,0.12)"}`,
      borderRadius:10, padding:"10px 12px",
      boxShadow: isDark?"0 8px 28px rgba(0,0,0,0.5)":"0 8px 28px rgba(0,0,0,0.12)",
      pointerEvents:"none",
    };
    if (dir === "right") return { ...base, top: Math.min(rect.top, vh - 200), left: rect.right + 10 };
    if (dir === "left")  return { ...base, top: Math.min(rect.top, vh - 200), left: rect.left - TIP_W - 10 };
    // bottom
    return { ...base, top: rect.bottom + 8, left: Math.min(rect.left, vw - TIP_W - 12) };
  }, [isMobile, pos, isDark]);

  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex" }}>
      <span
        onMouseEnter={isMobile ? undefined : show}
        onMouseLeave={isMobile ? undefined : hide}
        onTouchEnd={isMobile ? show : undefined}
        onClick={isMobile ? show : undefined}
        style={{ display:"inline-flex", cursor: isMobile?"pointer":"default" }}
      >
        {children}
      </span>
      {open && (
        <>
          {isMobile && (
            <div
              style={{ position:"fixed", inset:0, zIndex:9998, background:"rgba(0,0,0,0.3)" }}
              onClick={hide}
            />
          )}
          <div style={tipStyle}>
            {isMobile && (
              <button onClick={hide} style={{
                position:"absolute", top:10, right:12,
                background:"none", border:"none", cursor:"pointer",
                fontSize:16, color:"var(--muted)", padding:0, lineHeight:1,
              }}>✕</button>
            )}
            {content}
          </div>
        </>
      )}
    </span>
  );
}

// ── FILTER TOOLTIP ⓘ ──────────────────────────────────────────────────────────
function FilterTip({ tt, isDark, isMobile }) {
  const content = (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:C.amber, marginBottom:8, fontFamily:"var(--mono)" }}>◈ GUIDE ANALYSTE</div>
      {[["Description",tt.d],["Utilisation",tt.u],["Interprétation",tt.i]].map(([k,v])=>(
        <div key={k} style={{ marginBottom:5 }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"var(--muted)", marginBottom:2 }}>{k}</div>
          <div style={{ fontSize:11, color:"var(--text)", lineHeight:1.55 }}>{v}</div>
        </div>
      ))}
    </div>
  );
  return (
    <Tooltip content={content} isDark={isDark} isMobile={isMobile}>
      <button style={{
        background:"none", border:"none", cursor:"pointer", padding:"0 3px",
        color: isDark?"#334155":"#c4bfb4", fontSize:12, lineHeight:1, display:"flex", alignItems:"center",
      }}>ⓘ</button>
    </Tooltip>
  );
}

// ── PRIMITIVES ────────────────────────────────────────────────────────────────
function Pills({ options, value, onChange, isDark }) {
  return (
    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
      {options.map(o => {
        const a = value===o;
        return (
          <button key={o} onClick={() => onChange(o)} style={{
            padding:"3px 9px", fontSize:11, borderRadius:4, cursor:"pointer",
            fontFamily:"var(--mono)", whiteSpace:"nowrap",
            border: a ? `1px solid ${C.amber}88` : `1px solid ${isDark?"rgba(148,163,184,0.15)":"rgba(30,40,34,0.12)"}`,
            background: a ? "var(--pill-act)" : "var(--pill-bg)",
            color: a ? C.amber : "var(--muted)", transition:"all 0.12s",
          }}>{o}</button>
        );
      })}
    </div>
  );
}

function BoolTgl({ value, onChange, isDark }) {
  return (
    <div style={{ display:"flex", gap:5 }}>
      {[{v:null,l:"Tous"},{v:true,l:"Oui"},{v:false,l:"Non"}].map(({v,l}) => {
        const a = value===v;
        return (
          <button key={l} onClick={() => onChange(v)} style={{
            padding:"3px 10px", fontSize:11, borderRadius:4, cursor:"pointer", fontFamily:"var(--mono)",
            border: a ? `1px solid ${C.green}88` : `1px solid ${isDark?"rgba(148,163,184,0.15)":"rgba(30,40,34,0.12)"}`,
            background: a ? `${C.green}18` : "var(--pill-bg)",
            color: a ? C.green : "var(--muted)",
          }}>{l}</button>
        );
      })}
    </div>
  );
}

function RangeSlider({ f, value, onChange }) {
  const { min, max, unit="", step=1, isMax } = f;
  const pct = ((value-min)/(max-min))*100;
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:9, color:"var(--muted)", opacity:.5, fontFamily:"var(--mono)" }}>{min}{unit}</span>
        <span style={{ fontSize:12, color:C.amber, fontFamily:"var(--mono)", fontWeight:700 }}>{isMax?"≤ ":"≥ "}{value}{unit}</span>
        <span style={{ fontSize:9, color:"var(--muted)", opacity:.5, fontFamily:"var(--mono)" }}>{max}{unit}</span>
      </div>
      <div style={{ position:"relative", height:20, display:"flex", alignItems:"center" }}>
        <div style={{ position:"absolute", inset:"8px 0", height:4, background:"var(--sep)", borderRadius:2 }}>
          <div style={{ position:"absolute", left:`${isMax?pct:0}%`, width:`${isMax?100-pct:pct}%`, height:"100%", background:`${C.amber}66`, borderRadius:2 }}/>
        </div>
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ position:"absolute", inset:0, width:"100%", opacity:0, height:"100%", cursor:"pointer", zIndex:1 }}
        />
        <div style={{
          position:"absolute", left:`${pct}%`, top:"50%", transform:"translate(-50%,-50%)",
          width:14, height:14, borderRadius:"50%",
          background:C.amber, border:"2px solid var(--bg-from)",
          pointerEvents:"none", zIndex:2,
        }}/>
      </div>
    </div>
  );
}

function SigBadge({ signal }) {
  const t = signalTone(signal);
  const s = { pos:{bg:`${C.green}18`,bd:`${C.green}55`,c:C.green}, warn:{bg:`${C.orange}18`,bd:`${C.orange}55`,c:C.orange}, neg:{bg:`${C.red}16`,bd:`${C.red}55`,c:C.red} }[t];
  return (
    <span style={{ fontSize:9, fontWeight:800, padding:"2px 7px", borderRadius:4,
      background:s.bg, border:`1px solid ${s.bd}`, color:s.c,
      fontFamily:"var(--mono)", letterSpacing:"0.07em", whiteSpace:"nowrap" }}>{signal}</span>
  );
}

function ConvictionDots({ yes, watch, no }) {
  return (
    <div style={{ display:"flex", gap:2 }}>
      {Array.from({length:yes}).map((_,i)   => <span key={"y"+i} style={{ width:6,height:6,borderRadius:2,background:C.green,flexShrink:0 }}/>)}
      {Array.from({length:watch}).map((_,i) => <span key={"w"+i} style={{ width:6,height:6,borderRadius:2,background:C.amber,flexShrink:0 }}/>)}
      {Array.from({length:no}).map((_,i)    => <span key={"n"+i} style={{ width:6,height:6,borderRadius:2,background:C.red,flexShrink:0 }}/>)}
    </div>
  );
}

function MoatChip({ moat }) {
  const c = moat==="Wide"?C.green:moat==="Narrow"?C.amber:C.red;
  return (
    <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, fontFamily:"var(--mono)", fontWeight:700,
      background:`${c}18`, color:c, border:`1px solid ${c}44`, whiteSpace:"nowrap" }}>{moat}</span>
  );
}

function StatusIcon({ status }) {
  const map = { yes:[C.green,"✓"], watch:[C.amber,"◎"], no:[C.red,"✗"] };
  const [c,g] = map[status] || [C.amber,"?"];
  return <span style={{ color:c, fontSize:11, fontWeight:800, fontFamily:"var(--mono)", flexShrink:0 }}>{g}</span>;
}

// ── PILIER SECTION ─────────────────────────────────────────────────────────────
function PilierSection({ pilier, filterValues, setFV, isDark, isOpen, toggle, isMobile }) {
  return (
    <div style={{
      width:"100%", borderRadius:10,
      border:`1px solid ${isDark?"rgba(148,163,184,0.10)":"rgba(30,40,34,0.08)"}`,
      background: isDark?"rgba(15,23,42,0.48)":"rgba(255,255,255,0.75)",
    }}>
      <button onClick={toggle} style={{
        width:"100%", padding:"11px 14px", display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"none", border:"none", cursor:"pointer", textAlign:"left",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, color:C.amber, flexShrink:0 }}>{pilier.icon}</span>
          <span style={{ fontSize:12, fontWeight:700, color:"var(--text-hi)", letterSpacing:"0.03em" }}>{pilier.label}</span>
        </div>
        <span style={{ fontSize:10, color:"var(--muted)", transition:"transform 0.2s", display:"block", transform:isOpen?"rotate(180deg)":"none", flexShrink:0 }}>▾</span>
      </button>
      {isOpen && (
        <div style={{ padding:"0 14px 14px", borderTop:"1px solid var(--sep)", paddingTop:14, display:"flex", flexDirection:"column", gap:14 }}>
          {pilier.filters.map(f => (
            <div key={f.id}>
              <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:6 }}>
                <span style={{ fontSize:10, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.10em", fontFamily:"var(--mono)", fontWeight:600 }}>{f.label}</span>
                <FilterTip tt={f.tt} isDark={isDark} isMobile={isMobile}/>
              </div>
              {f.type==="pills" && <Pills options={f.options} value={filterValues[f.id]} onChange={v=>setFV(f.id,v)} isDark={isDark}/>}
              {f.type==="range" && <RangeSlider f={f} value={filterValues[f.id]} onChange={v=>setFV(f.id,v)}/>}
              {f.type==="bool"  && <BoolTgl value={filterValues[f.id]} onChange={v=>setFV(f.id,v)} isDark={isDark}/>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TABLE HORIZONTALE (desktop ET mobile scroll) ───────────────────────────────
// Colonnes décisionnelles choisies par ordre d'importance analytique :
// 1. Signal + conviction (décision immédiate)
// 2. IScore (score composite global)
// 3. EVA / ROIC-WACC (création de valeur — discriminant fondamental #1)
// 4. MOS + label (valorisation — décote ou survaluation)
// 5. TScore + label (timing d'entrée)
// 6. CAGR 5Y TR (performance historique)
// 7. DD Max (risque de perte)
// 8. YTD (momentum court terme visible)
// 9. Moat (qualité structurelle)

// Total: 148+84+130+64+96+100+62+68+64+56+60 = 932px → scroll confortable sur mobile
const TABLE_COLS = [
  { key:null,           label:"Titre",          w:148, align:"left",  grow:true },
  { key:"dd_signal",    label:"Signal",         w:84,  align:"left"  },
  { key:"iscore",       label:"IScore · Fond.", w:130, align:"left"  },
  { key:"roic_vs_wacc", label:"EVA",            w:64,  align:"right" },
  { key:"mos",          label:"MOS",            w:96,  align:"right" },
  { key:"tscore",       label:"Timing",         w:100, align:"left"  },
  { key:"bt_5y_cagr_tr",label:"CAGR 5Y",        w:68,  align:"right" },
  { key:"er_proxy",     label:"ER AQR",         w:62,  align:"right" },
  { key:"rr_dd_max",    label:"DD Max",         w:64,  align:"right" },
  { key:"ytd_pct",      label:"YTD",            w:56,  align:"right" },
  { key:null,           label:"Moat",           w:60,  align:"right" },
];

function ResultTable({ stocks, sortKey, sortDir, onSort, isDark, isMobile, onSelectStock }) {
  const totalW = TABLE_COLS.reduce((s,c)=>s+c.w,0) + 32; // 16px padding each side

  return (
    <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch", width:"100%", maxWidth:"100%",
      borderRadius:10, border:"1px solid var(--card-bd)", background:"var(--card-bg)" }}>
      <div style={{ minWidth: totalW, width:"100%" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", padding:"7px 16px",
          borderBottom:"2px solid var(--sep)",
          background: isDark?"rgba(11,18,32,0.85)":"rgba(246,245,241,0.95)",
          position:"sticky", top:0, zIndex:10, width:"100%" }}>
          {TABLE_COLS.map((col,i) => (
            <div key={i} onClick={()=>col.key&&onSort(col.key)} style={{
              width: col.grow ? undefined : col.w,
              minWidth: col.grow ? col.w : undefined,
              flex: col.grow ? 1 : undefined,
              flexShrink:0,
              fontSize:9, fontFamily:"var(--mono)", letterSpacing:"0.10em", textTransform:"uppercase",
              color: sortKey===col.key ? C.amber : "var(--muted)",
              cursor: col.key?"pointer":"default", userSelect:"none",
              display:"flex", alignItems:"center", gap:3,
              justifyContent: col.align==="right" ? "flex-end" : "flex-start",
              borderLeft: col.key==="tscore" ? "1px solid var(--sep)" : undefined,
              paddingLeft: col.key==="tscore" ? 6 : undefined,
              borderRight: col.key==="mos" ? "1px solid var(--sep)" : undefined,
              paddingRight: col.key==="mos" ? 6 : undefined,
            }}>
              {col.label}
              {col.key && sortKey===col.key && <span style={{fontSize:7}}>{sortDir==="desc"?"▼":"▲"}</span>}
            </div>
          ))}
        </div>

        {/* Rows */}
        {stocks.map((s,i) => (
          <TableRow key={s.symbol} stock={s} rank={i+1} isDark={isDark} isMobile={isMobile} onSelectStock={onSelectStock}/>
        ))}
      </div>
    </div>
  );
}

function TableRow({ stock, rank, isDark, isMobile, onSelectStock }) {
  const [hov, setHov] = useState(false);

  const decisionContent = (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:C.amber, marginBottom:8, fontFamily:"var(--mono)" }}>◈ FACTEURS DÉCISION</div>
      {[
        ["Marché",       stock.dd_marche_status,    stock.dd_marche_label],
        ["Moat",         stock.dd_moat_status,      stock.dd_moat_label],
        ["Valeur",       stock.dd_value_status,     stock.dd_value_label],
        ["Momentum",     stock.dd_momentum_status,  stock.dd_momentum_label],
        ["R/R",          stock.dd_rr_status,        stock.dd_rr_label],
        ["Valorisation", stock.dd_valuation_status, stock.dd_valuation_label],
      ].map(([l,st,d]) => (
        <div key={l} style={{ display:"flex", gap:6, marginBottom:5 }}>
          <StatusIcon status={st}/>
          <div><span style={{ fontSize:10, fontWeight:700, color:"var(--text)" }}>{l}</span>
            <span style={{ fontSize:10, color:"var(--muted)" }}> — {d}</span></div>
        </div>
      ))}
      <div style={{ marginTop:8, padding:"6px 8px", borderRadius:6,
        background:isDark?"rgba(15,23,42,0.5)":"rgba(248,246,242,0.8)",
        fontSize:10, color:"var(--text)", lineHeight:1.5, fontStyle:"italic" }}>{stock.dd_summary}</div>
    </div>
  );

  const fundTip = (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:C.amber, marginBottom:6, fontFamily:"var(--mono)" }}>▣ FONDAMENTAUX</div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Score global : <span style={{ color:scoreColor(stock.fund_score), fontWeight:700 }}>{stock.fund_score.toFixed(1)}/100</span></div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Création valeur : <span style={{ color:scoreColor(stock.fund_value_creation), fontWeight:700 }}>{stock.fund_value_creation.toFixed(1)}/100</span></div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Solvabilité : <span style={{ color:scoreColor(stock.fund_solvency), fontWeight:700 }}>{stock.fund_solvency.toFixed(1)}/100</span></div>
      <div style={{ fontSize:11, color:"var(--muted)" }}>EVA : <span style={{ color:evaColor(stock.roic_vs_wacc), fontWeight:700 }}>{stock.roic_vs_wacc > 0 ? "+" : ""}{stock.roic_vs_wacc.toFixed(1)} pts ROIC-WACC</span></div>
      <div style={{ marginTop:5, fontSize:10, color:scoreColor(stock.fund_score), fontWeight:700 }}>{stock.fund_label}</div>
    </div>
  );

  const erContent = (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:C.amber, marginBottom:6, fontFamily:"var(--mono)" }}>◈ RENDEMENT ATTENDU AQR</div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Proxy : <span style={{ color:erColor(stock.er_proxy), fontWeight:700 }}>{stock.er_proxy > 0 ? "+" : ""}{stock.er_proxy.toFixed(2)}%</span></div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Driver : <span style={{ fontWeight:700, color:"var(--text)" }}>{stock.top_driver || "—"}</span></div>
      <div style={{ fontSize:11, color:"var(--muted)" }}>Headwind : <span style={{ color:"var(--text)" }}>{stock.top_headwind || "—"}</span></div>
    </div>
  );

  const evaContent = (
    <div>
      <div style={{ fontSize:10, fontWeight:700, color:C.amber, marginBottom:6, fontFamily:"var(--mono)" }}>▣ CRÉATION DE VALEUR</div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>ROIC–WACC : <span style={{ color:evaColor(stock.roic_vs_wacc), fontWeight:700 }}>{stock.roic_vs_wacc>0?"+":""}{stock.roic_vs_wacc.toFixed(1)} pts</span></div>
      <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3 }}>Score EVA : <span style={{ color:scoreColor(stock.fund_value_creation), fontWeight:700 }}>{stock.fund_value_creation.toFixed(0)}/100</span></div>
      <div style={{ fontSize:11, color:"var(--muted)" }}>Fondamentaux : <span style={{ color:scoreColor(stock.fund_score), fontWeight:700 }}>{stock.fund_label}</span></div>
      <div style={{ marginTop:5, fontSize:10, color:"var(--muted)" }}>
        {stock.roic_vs_wacc >= 5 ? "✓ EVA positive — création de valeur structurelle" :
         stock.roic_vs_wacc >= 0 ? "◎ EVA nulle — à surveiller" :
         "✗ EVA négative — destruction de valeur"}
      </div>
    </div>
  );

  return (
    <div
      role={onSelectStock ? "button" : undefined}
      tabIndex={onSelectStock ? 0 : undefined}
      onClick={() => onSelectStock?.(stock.symbol)}
      onKeyDown={(e) => { if (onSelectStock && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onSelectStock(stock.symbol); } }}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        display:"flex", alignItems:"center", padding:"11px 16px", gap:0,
        borderBottom:"1px solid var(--sep)",
        background: hov?"var(--row-hov)":rank%2===0?"var(--row-alt)":"transparent",
        transition:"background 0.1s",
        width:"100%",
        cursor: onSelectStock ? "pointer" : "default",
      }}
    >
      {/* Titre — w:148, grows to fill */}
      <div style={{ flex:1, minWidth:148, flexShrink:0 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--text-hi)", fontFamily:"var(--mono)", letterSpacing:"0.04em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{stock.symbol}</div>
        <div style={{ fontSize:9, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{stock.company}</div>
        <div style={{ fontSize:8, color:"var(--muted)", opacity:.6, fontFamily:"var(--mono)" }}>{stock.sector.slice(0,14)}</div>
      </div>

      {/* Signal + conviction — w:84 */}
      <div style={{ width:84, flexShrink:0, display:"flex", flexDirection:"column", gap:4 }}>
        <Tooltip content={decisionContent} isDark={isDark} isMobile={isMobile}>
          <div style={{ cursor:"help", display:"inline-flex", flexDirection:"column", gap:3 }}>
            <SigBadge signal={stock.dd_signal}/>
            <ConvictionDots yes={stock.dd_yes} watch={stock.dd_watch} no={stock.dd_no}/>
          </div>
        </Tooltip>
      </div>

      {/* IScore + Fondamentaux — w:130 */}
      <div style={{ width:130, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center" }}>
          <div style={{ width:34, flexShrink:0, textAlign:"center" }}>
            <span style={{ fontSize:15, fontWeight:800, color:scoreColor(stock.iscore), fontFamily:"var(--mono)", lineHeight:1 }}>{stock.iscore.toFixed(0)}</span>
            <div style={{ fontSize:7, color:"var(--muted)", fontFamily:"var(--mono)", marginTop:1 }}>IScore</div>
          </div>
          <div style={{ width:1, height:28, background:"var(--sep)", flexShrink:0, margin:"0 8px" }}/>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ flex:1, height:4, background:"var(--sep)", borderRadius:2 }}>
                <div style={{ width:Math.min(100,stock.fund_score)+"%", height:"100%", background:scoreColor(stock.fund_score), borderRadius:2 }}/>
              </div>
              <span style={{ fontSize:11, color:scoreColor(stock.fund_score), fontFamily:"var(--mono)", fontWeight:700, flexShrink:0 }}>{stock.fund_score.toFixed(0)}</span>
            </div>
            <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)", marginTop:2 }}>Fond.</div>
            <div style={{ fontSize:8, color:scoreColor(stock.fund_value_creation), fontFamily:"var(--mono)", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              EVA {stock.roic_vs_wacc>0?"+":""}{stock.roic_vs_wacc.toFixed(1)}pts
            </div>
          </div>
        </div>
      </div>

      {/* EVA — w:64 */}
      <Tooltip content={evaContent} isDark={isDark} isMobile={isMobile}>
        <div style={{ width:64, flexShrink:0, textAlign:"right", cursor:"help", paddingRight:4 }}>
          <div style={{ fontSize:11, fontWeight:700, color:evaColor(stock.roic_vs_wacc), fontFamily:"var(--mono)" }}>
            {stock.roic_vs_wacc>0?"+":""}{stock.roic_vs_wacc.toFixed(1)}
          </div>
          <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>EVA pts</div>
        </div>
      </Tooltip>

      {/* MOS — w:96 */}
      <div style={{ width:96, flexShrink:0, textAlign:"right", paddingRight:6, borderRight:"1px solid var(--sep)" }}>
        <div style={{ fontSize:11, fontWeight:700, color:mosColor(stock.mos), fontFamily:"var(--mono)" }}>
          {stock.mos>0?"+":""}{stock.mos.toFixed(1)}%
        </div>
        <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>MOS</div>
      </div>

      {/* Timing — w:100 */}
      <div style={{ width:100, flexShrink:0, borderLeft:"1px solid var(--sep)", paddingLeft:6 }}>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <div style={{ width:34, height:4, background:"var(--sep)", borderRadius:2, flexShrink:0 }}>
            <div style={{ width:`${Math.min(100,stock.tscore)}%`, height:"100%", background:scoreColor(stock.tscore), borderRadius:2 }}/>
          </div>
          <span style={{ fontSize:11, color:scoreColor(stock.tscore), fontFamily:"var(--mono)", fontWeight:700 }}>{stock.tscore.toFixed(0)}</span>
        </div>
        <div style={{ fontSize:8, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:96, marginTop:2 }}>
          {stock.tscore_label.split("·")[0].trim().slice(0,16)}
        </div>
      </div>

      {/* CAGR 5Y — w:68 */}
      <div style={{ width:68, flexShrink:0, textAlign:"right" }}>
        <div style={{ fontSize:11, fontWeight:700, color:cagrColor(stock.bt_5y_cagr_tr), fontFamily:"var(--mono)" }}>
          {stock.bt_5y_cagr_tr>0?"+":""}{stock.bt_5y_cagr_tr.toFixed(1)}%
        </div>
        <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>5Y TR</div>
      </div>

      {/* ER AQR — w:62 */}
      <Tooltip content={erContent} isDark={isDark} isMobile={isMobile}>
        <div style={{ width:62, flexShrink:0, textAlign:"right", cursor:"help" }}>
          <div style={{ fontSize:11, fontWeight:700, color:erColor(stock.er_proxy), fontFamily:"var(--mono)" }}>
            {stock.er_proxy > 0 ? "+" : ""}{stock.er_proxy.toFixed(1)}%
          </div>
          <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>ER AQR</div>
        </div>
      </Tooltip>

      {/* DD Max — w:64 */}
      <div style={{ width:64, flexShrink:0, textAlign:"right" }}>
        <div style={{ fontSize:12, fontWeight:700, color:ddColor(stock.rr_dd_max), fontFamily:"var(--mono)" }}>
          {stock.rr_dd_max.toFixed(1)}%
        </div>
        <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>DD Max</div>
      </div>

      {/* YTD — w:56 */}
      <div style={{ width:56, flexShrink:0, textAlign:"right" }}>
        <div style={{ fontSize:12, fontWeight:700, color:pctColor(stock.ytd_pct), fontFamily:"var(--mono)" }}>
          {stock.ytd_pct>0?"+":""}{stock.ytd_pct.toFixed(1)}%
        </div>
        <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)" }}>YTD</div>
      </div>

      {/* Moat — w:60 */}
      <div style={{ width:60, flexShrink:0, textAlign:"right" }}>
        <MoatChip moat={stock.moat}/>
      </div>
    </div>
  );
}

// ── SORT BAR (mobile only) ────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { k:"iscore",        l:"IScore"   },
  { k:"roic_vs_wacc",  l:"EVA"      },
  { k:"mos",           l:"MOS"      },
  { k:"tscore",        l:"Timing"   },
  { k:"bt_5y_cagr_tr", l:"CAGR 5Y" },
  { k:"rr_dd_max",     l:"DD Max"   },
  { k:"ytd_pct",       l:"YTD"      },
];

function MobileResultCards({ stocks, onSelectStock }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8, width:"100%" }}>
      {stocks.map((stock) => (
        <button
          key={stock.symbol}
          type="button"
          onClick={() => onSelectStock?.(stock.symbol)}
          style={{
            width:"100%", textAlign:"left", cursor: onSelectStock ? "pointer" : "default",
            borderRadius:10, border:"1px solid var(--card-bd)", background:"var(--card-bg)",
            padding:"12px 14px", display:"flex", flexDirection:"column", gap:8,
          }}
        >
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontSize:12, fontWeight:800, color:"var(--text-hi)", fontFamily:"var(--mono)" }}>{stock.symbol}</div>
              <div style={{ fontSize:10, color:"var(--muted)", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{stock.company}</div>
            </div>
            <SigBadge signal={stock.dd_signal}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:6 }}>
            {[
              ["IScore", stock.iscore != null ? Number(stock.iscore).toFixed(0) : "—", scoreColor(stock.iscore)],
              ["MOS", `${(stock.mos ?? 0) > 0 ? "+" : ""}${Number(stock.mos || 0).toFixed(0)}%`, mosColor(stock.mos)],
              ["EVA", `${(stock.roic_vs_wacc ?? 0) > 0 ? "+" : ""}${Number(stock.roic_vs_wacc || 0).toFixed(0)}`, evaColor(stock.roic_vs_wacc)],
              ["YTD", `${(stock.ytd_pct ?? 0) > 0 ? "+" : ""}${Number(stock.ytd_pct || 0).toFixed(1)}%`, pctColor(stock.ytd_pct)],
            ].map(([l, v, c]) => (
              <div key={l} style={{ borderRadius:6, background:"var(--row-alt)", padding:"6px 4px", textAlign:"center" }}>
                <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"var(--mono)", textTransform:"uppercase" }}>{l}</div>
                <div style={{ fontSize:11, fontWeight:800, color:c, fontFamily:"var(--mono)", marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
}

function MobileSortBar({ sortKey, sortDir, onSort, isDark }) {
  return (
    <div style={{
      display:"flex", gap:5, overflowX:"auto", paddingBottom:6,
      WebkitOverflowScrolling:"touch",
    }}>
      {SORT_OPTIONS.map(({k,l}) => {
        const a = sortKey===k;
        return (
          <button key={k} onClick={()=>onSort(k)} style={{
            flexShrink:0, padding:"4px 10px", fontSize:10, borderRadius:4,
            border:`1px solid ${a?C.amber+"66":"var(--card-bd)"}`,
            background: a?`${C.amber}14`:"var(--card-bg)",
            color: a?C.amber:"var(--muted)",
            cursor:"pointer", fontFamily:"var(--mono)",
            display:"flex", alignItems:"center", gap:4,
          }}>
            {l}
            {a && <span style={{fontSize:7}}>{sortDir==="desc"?"▼":"▲"}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function InvelpsScreener({
  embedded = false,
  nav,
  openTicker,
  isDark: isDarkProp,
  setDark: setDarkProp,
  shellTh,
  stocks: stocksProp,
} = {}) {
  const getAutoDark = () => { const h=new Date().getHours(); return h<6||h>=20; };
  const [internalDark, setInternalDark] = useState(getAutoDark);
  const isDark = isDarkProp ?? internalDark;
  const setIsDark = setDarkProp ?? setInternalDark;
  const dataset = useMemo(() => {
    const raw = stocksProp != null ? stocksProp : embedded ? [] : MOCK;
    return raw.map(normalizeScreenerStock);
  }, [stocksProp, embedded]);
  const piliers = useMemo(() => buildPiliers(dataset), [dataset]);
  const savedUi = useMemo(() => loadScreenerState(), []);

  const [isMobile, setMobile]  = useState(false);
  const [opens,    setOpens]   = useState(
    () => (Array.isArray(savedUi?.opens) && savedUi.opens.length ? savedUi.opens : ["profil", "decisions"])
  );
  const [panelOpen,setPanel]   = useState(() => Boolean(savedUi?.panelOpen));
  const [sortKey,  setSortKey] = useState(() => savedUi?.sortKey || "iscore");
  const [sortDir,  setSortDir] = useState(() => savedUi?.sortDir || "desc");
  const [page,     setPage]    = useState(() => (typeof savedUi?.page === "number" ? savedUi.page : 0));
  const PAGE_SIZE = 20;
  const filtersRestoredRef = useRef(false);

  useEffect(() => { applyTheme(isDark, embedded ? shellTh : null); }, [isDark, embedded, shellTh]);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 820);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!isMobile) setPanel(true);
    else if (embedded) setPanel(false);
  }, [isMobile, embedded]);

  const defFilters = useMemo(() => {
    const d={};
    piliers.forEach(p=>p.filters.forEach(f=>{
      if (f.type==="pills") d[f.id]="Tous";
      else if (f.type==="bool") d[f.id]=null;
      else d[f.id] = f.isMax ? f.max : f.min;
    }));
    return d;
  }, [piliers]);

  const [fv, setFvAll] = useState(() =>
    mergeScreenerFilterValues(savedUi?.fv, defFilters)
  );
  const setFV = (id, v) => setFvAll((p) => ({ ...p, [id]: v }));

  useEffect(() => {
    if (filtersRestoredRef.current || !Object.keys(defFilters).length) return;
    const saved = loadScreenerState();
    if (saved?.fv) {
      setFvAll(mergeScreenerFilterValues(saved.fv, defFilters));
    }
    if (saved?.sortKey) setSortKey(saved.sortKey);
    if (saved?.sortDir) setSortDir(saved.sortDir);
    if (typeof saved?.page === "number") setPage(saved.page);
    if (Array.isArray(saved?.opens) && saved.opens.length) setOpens(saved.opens);
    if (typeof saved?.panelOpen === "boolean") setPanel(saved.panelOpen);
    filtersRestoredRef.current = true;
  }, [defFilters]);

  useEffect(() => {
    saveScreenerState({ fv, sortKey, sortDir, page, opens, panelOpen });
  }, [fv, sortKey, sortDir, page, opens, panelOpen]);

  const resetFilters = useCallback(() => {
    clearScreenerState();
    setFvAll(defFilters);
    setPage(0);
  }, [defFilters]);

  const onSelectStock = useCallback((sym) => {
    saveScreenerState({ fv, sortKey, sortDir, page, opens, panelOpen });
    if (openTicker) openTicker(sym);
    else if (nav) nav(`/stock/${encodeURIComponent(sym)}`);
  }, [nav, openTicker, fv, sortKey, sortDir, page, opens, panelOpen]);

  const toggleSort = key => {
    if (sortKey===key) setSortDir(d=>d==="desc"?"asc":"desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const allOpen   = opens.length === piliers.length;
  const toggleAll = () => setOpens(allOpen ? [] : piliers.map(p=>p.id));
  const toggleOne = id => setOpens(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  const filtered = useMemo(() => {
    return dataset.filter(s => {
      for (const pilier of piliers) {
        for (const f of pilier.filters) {
          const val = fv[f.id];
          if (f.type==="pills") {
            if (val==="Tous") continue;
            if (f.id==="region") {
              const { regionFr } = resolveStockRegion(s);
              if (regionFr !== val) return false;
            } else if (s[f.field] !== val) return false;
          }
          else if (f.type==="bool") {
            if (val!==null) {
              const sv = f.invert ? !s[f.field] : s[f.field];
              if (val===true&&!sv) return false;
              if (val===false&&sv) return false;
            }
          } else if (f.type==="range") {
            const sv = s[f.field]??0;
            if (f.isMax) { if(sv>val) return false; }
            else { if(sv<val) return false; }
          }
        }
      }
      return true;
    }).sort((a,b) => {
      const av=a[sortKey]??0, bv=b[sortKey]??0;
      return sortDir==="desc"?bv-av:av-bv;
    });
  }, [fv, sortKey, sortDir, dataset, piliers]);

  useEffect(() => { setPage(0); }, [filtered]);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageWindow = useMemo(
    () => getPaginationWindow(page, totalPages, isMobile ? 5 : 7),
    [page, totalPages, isMobile]
  );
  const paginated  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const activeCount = useMemo(() => {
    let n=0;
    piliers.forEach(p=>p.filters.forEach(f=>{
      const v=fv[f.id];
      if(f.type==="pills"&&v!=="Tous") n++;
      else if(f.type==="bool"&&v!==null) n++;
      else if(f.type==="range") { const def=f.isMax?f.max:f.min; if(v!==def) n++; }
    }));
    return n;
  }, [fv, piliers]);

  return (
    <div style={{
      minHeight: embedded ? undefined : "100vh",
      width:"100%", maxWidth:"100%",
      overflowX:"hidden",
      background: embedded ? "transparent" : "linear-gradient(to bottom, var(--bg-from), var(--bg-to))",
      color:"var(--text-hi)", fontFamily:"'IBM Plex Sans',sans-serif",
    }}>
      {!embedded && (
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;600&family=Cormorant+Garamond:wght@600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html,body,#root{width:100%;min-width:0;overflow-x:hidden;}
        ::-webkit-scrollbar{width:3px;height:3px;}
        ::-webkit-scrollbar-thumb{background:rgba(181,154,91,0.3);border-radius:2px;}
        input[type=range]{-webkit-appearance:none;height:20px;background:transparent;outline:none;}
        button{font-family:inherit;}
      `}</style>
      )}

      {!embedded && (
      <div style={{
        position:"sticky", top:0, zIndex:40,
        width:"100%", boxSizing:"border-box",
        backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
        background:"var(--nav-bg)", borderBottom:"1px solid var(--sep)",
        padding: isMobile?"10px 14px":"12px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28,height:28,borderRadius:6,flexShrink:0,
            background:`linear-gradient(135deg,${C.green},${C.amber})`,
            display:"flex",alignItems:"center",justifyContent:"center" }}>
            <span style={{ fontSize:13,fontWeight:800,color:"#fff",fontFamily:"var(--mono)" }}>I</span>
          </div>
          <div>
            <div style={{ fontSize:isMobile?11:13,fontWeight:700,letterSpacing:"0.08em",color:"var(--text-hi)",fontFamily:"var(--mono)" }}>
              INVELPS<span style={{color:C.amber}}>·</span>SCREENER
            </div>
            {!isMobile && <div style={{ fontSize:9,color:"var(--muted)",fontFamily:"var(--mono)",letterSpacing:"0.08em" }}>MULTI-FACTOR · 10 PILIERS</div>}
          </div>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          {activeCount>0 && (
            <span style={{ fontSize:10,padding:"2px 8px",borderRadius:20,background:`${C.amber}22`,border:`1px solid ${C.amber}55`,color:C.amber,fontFamily:"var(--mono)",fontWeight:700 }}>
              {activeCount} filtre{activeCount>1?"s":""}
            </span>
          )}
          <span style={{ fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)" }}>
            <span style={{color:C.amber,fontWeight:700}}>{filtered.length}</span>/{dataset.length}
          </span>
          {!setDarkProp && (
          <button onClick={()=>setIsDark(d=>!d)} style={{
            padding:"5px 10px", border:"1px solid var(--card-bd)", background:"var(--card-bg)",
            color:"var(--muted)", borderRadius:6, cursor:"pointer", fontSize:13,
            display:"flex", alignItems:"center", gap:5,
          }}>
            {isDark?"☀":"☾"}
            {!isMobile && <span style={{fontSize:10,fontFamily:"var(--mono)"}}>{isDark?"Jour":"Nuit"}</span>}
          </button>
          )}
          {isMobile && (
            <button onClick={()=>setPanel(p=>!p)} style={{
              padding:"5px 10px", fontSize:10, fontFamily:"var(--mono)",
              border:`1px solid ${activeCount>0?C.amber+"66":"var(--card-bd)"}`,
              background: activeCount>0?`${C.amber}12`:"var(--card-bg)",
              color: activeCount>0?C.amber:"var(--muted)",
              borderRadius:6, cursor:"pointer",
            }}>⊟ {panelOpen?"Masquer":"Filtres"}</button>
          )}
        </div>
      </div>
      )}

      {embedded && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:"var(--text-hi)", fontFamily:"var(--serif)" }}>Screener multi-facteurs</div>
            <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--mono)" }}>10 piliers · {filtered.length}/{dataset.length} titres</div>
          </div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            {activeCount>0 && (
              <span style={{ fontSize:10,padding:"2px 8px",borderRadius:20,background:`${C.amber}22`,border:`1px solid ${C.amber}55`,color:C.amber,fontFamily:"var(--mono)",fontWeight:700 }}>
                {activeCount} filtre{activeCount>1?"s":""}
              </span>
            )}
            {isMobile && (
              <button onClick={()=>setPanel(p=>!p)} style={{
                padding:"5px 10px", fontSize:10, fontFamily:"var(--mono)",
                border:`1px solid ${C.amber}66`, background:`${C.amber}12`, color:C.amber,
                borderRadius:6, cursor:"pointer",
              }}>⊟ {panelOpen?"Masquer":"Filtres"}</button>
            )}
          </div>
        </div>
      )}

      {/* ── LAYOUT ── */}
      <div style={{
        width:"100%", maxWidth:"100%",
        padding: embedded ? 0 : (isMobile?"12px":"16px 24px"),
        display:"flex",
        flexDirection: isMobile?"column":"row",
        gap: isMobile?12:20,
        alignItems:"start",
        boxSizing:"border-box",
      }}>

        {/* ── SIDEBAR FILTRES ── */}
        {(!isMobile || panelOpen) && (
          <div style={{
              width: isMobile?"100%":280, flexShrink:0,
              display:"flex", flexDirection:"column",
              position: isMobile?"static":"sticky",
              top: isMobile?undefined:(embedded?12:72),
            }}>

            {/* Header: Filtres + expand/collapse + reset */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, padding:"0 2px" }}>
              <span style={{ fontSize:9, fontFamily:"var(--mono)", color:"var(--muted)", letterSpacing:"0.1em", textTransform:"uppercase" }}>Filtres</span>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={toggleAll} style={{
                  fontSize:9, padding:"3px 9px",
                  border:`1px solid ${isDark?"rgba(148,163,184,0.20)":"rgba(30,40,34,0.12)"}`,
                  background:"var(--pill-bg)", color:"var(--muted)",
                  borderRadius:4, cursor:"pointer", fontFamily:"var(--mono)",
                  display:"flex", alignItems:"center", gap:4,
                }}>{allOpen ? "▲ Réduire" : "▼ Ouvrir"}</button>
                <button onClick={resetFilters} style={{
                  fontSize:9, padding:"3px 8px",
                  border:`1px solid ${C.red}44`,
                  background:`${C.red}10`, color:C.red,
                  borderRadius:4, cursor:"pointer", fontFamily:"var(--mono)",
                }}>↺ Reset</button>
              </div>
            </div>

            {/* Scrollable piliers — hauteur alignée sur la table (20 lignes ≈ 1100px) */}
            <div style={{
              maxHeight: isMobile?undefined:"calc(100vh - 140px)",
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch",
              display:"flex", flexDirection:"column", gap:6,
              paddingRight: isMobile?0:3,
              paddingBottom: 16,
            }}>
              {piliers.map(p => (
                <PilierSection
                  key={p.id} pilier={p} filterValues={fv} setFV={setFV}
                  isDark={isDark} isOpen={opens.includes(p.id)} toggle={()=>toggleOne(p.id)}
                  isMobile={isMobile}
                />
              ))}
            </div>

          </div>
        )}

        {/* ── RÉSULTATS ── */}
        <div style={{ flex:"1 1 auto", minWidth:0, width:"100%", overflow: isMobile ? "visible" : "hidden" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,padding:"0 2px" }}>
            <div>
              <span style={{ fontSize:isMobile?13:16,fontWeight:700,color:"var(--text-hi)",fontFamily:"var(--serif)" }}>Résultats</span>
              <span style={{ fontSize:11,color:"var(--muted)",marginLeft:10,fontFamily:"var(--mono)" }}>{filtered.length} titre{filtered.length>1?"s":""}</span>
            </div>
            {!isMobile && (
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--mono)", display:"flex", alignItems:"center", gap:4 }}>
                <span style={{opacity:.5}}>clic colonne pour trier</span>
              </div>
            )}
          </div>

          {/* Sort bar (mobile) */}
          {isMobile && <div style={{ marginBottom:10 }}><MobileSortBar sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} isDark={isDark}/></div>}

          {filtered.length===0 ? (
            <div style={{ padding:"48px 24px",textAlign:"center",border:"1px solid var(--sep)",borderRadius:10,background:"var(--card-bg)" }}>
              <div style={{ fontSize:24,marginBottom:10,color:"var(--muted)" }}>◌</div>
              <div style={{ fontSize:13,color:"var(--muted)",fontFamily:"var(--mono)" }}>Aucun titre ne correspond aux critères.</div>
              <button onClick={resetFilters} style={{
                marginTop:14,fontSize:11,padding:"6px 16px",
                border:`1px solid ${C.amber}55`,background:`${C.amber}12`,
                color:C.amber,borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)",
              }}>↺ Réinitialiser</button>
            </div>
          ) : isMobile ? (
            <MobileResultCards stocks={paginated} onSelectStock={onSelectStock} />
          ) : (
            <ResultTable
              stocks={paginated} sortKey={sortKey} sortDir={sortDir}
              onSort={toggleSort} isDark={isDark} isMobile={isMobile} onSelectStock={onSelectStock}
            />
          )}

          {/* ── PAGINATION ── */}
          {totalPages > 1 && (
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"center",
              gap:8, marginTop:12, paddingBottom:8,
            }}>
              <button
                onClick={()=>setPage(p=>Math.max(0,p-1))}
                disabled={page===0}
                style={{
                  padding:"5px 12px", fontSize:11, borderRadius:6, cursor:page===0?"not-allowed":"pointer",
                  border:`1px solid ${page===0?"var(--sep)":C.amber+"66"}`,
                  background: page===0?"transparent":`${C.amber}12`,
                  color: page===0?"var(--muted)":C.amber,
                  fontFamily:"var(--mono)", opacity:page===0?0.4:1,
                }}>← Préc.</button>

              <div style={{ display:"flex", gap:4, flexWrap:"wrap", justifyContent:"center", maxWidth:isMobile?"min(100%, 220px)":"none" }}>
                {pageWindow[0] > 0 && (
                  <span style={{ fontSize:10, color:"var(--muted)", alignSelf:"center", padding:"0 2px" }}>…</span>
                )}
                {pageWindow.map((i) => (
                  <button key={i} onClick={()=>setPage(i)} style={{
                    minWidth: isMobile ? 36 : 28,
                    height: isMobile ? 36 : 28,
                    borderRadius: 6,
                    fontSize: isMobile ? 12 : 11,
                    border:`1px solid ${i===page?C.amber+"88":"var(--sep)"}`,
                    background: i===page?`${C.amber}20`:"transparent",
                    color: i===page?C.amber:"var(--muted)",
                    cursor:"pointer", fontFamily:"var(--mono)", fontWeight: i===page?700:400,
                  }}>{i+1}</button>
                ))}
                {pageWindow.length > 0 && pageWindow[pageWindow.length - 1] < totalPages - 1 && (
                  <span style={{ fontSize:10, color:"var(--muted)", alignSelf:"center", padding:"0 2px" }}>…</span>
                )}
              </div>

              <button
                onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))}
                disabled={page===totalPages-1}
                style={{
                  padding:"5px 12px", fontSize:11, borderRadius:6, cursor:page===totalPages-1?"not-allowed":"pointer",
                  border:`1px solid ${page===totalPages-1?"var(--sep)":C.amber+"66"}`,
                  background: page===totalPages-1?"transparent":`${C.amber}12`,
                  color: page===totalPages-1?"var(--muted)":C.amber,
                  fontFamily:"var(--mono)", opacity:page===totalPages-1?0.4:1,
                }}>Suiv. →</button>

              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--mono)", marginLeft:4 }}>
                {page*PAGE_SIZE+1}–{Math.min((page+1)*PAGE_SIZE, filtered.length)} / {filtered.length}
              </span>
            </div>
          )}


        </div>
      </div>
    </div>
  );
}
